import { BOOKMAKER_ALIASES, DEFAULT_MARKET, isGameFinished, getDefaultMarketForSport, isExcludedFromBestBook, EXCLUDED_FROM_BESTBOOK } from './constants';

/**
 * Check if market is binary (yes/no) vs numeric (over/under with point values)
 * Binary markets don't have mainline/alternate versions, so use API's bestBookDiff
 * Numeric markets can have alternates, so we recalculate client-side
 */
export const isBinaryMarket = (market) => {
  return market && (
    market.includes('scorer') ||
    market.includes('first') ||
    market.includes('1st') ||
    market.includes('last') ||
    market.includes('anytime') ||
    market.includes('double_double') ||
    market.includes('triple_double')
  );
};

/**
 * Recalculate edge for a specific player/book/market by comparing only this line's odds
 * This avoids mixing mainline and alternate market data
 * For binary markets, don't pass a side (it will use direct field name)
 */
export const recalculateEdge = (player, book, market, bookmakers, side = null) => {
  if (!player || !book || !market) return 0;

  // For binary markets (no side), use direct field name; for numeric markets, append side
  const bookOdds = side ? player[`${market}_${book}_${side}`] : player[`${market}_${book}`];
  if (!bookOdds) return 0;

  // Get all books' odds for this specific line
  const allOddsForThisLine = [];
  bookmakers.forEach(b => {
    const odds = side ? player[`${market}_${b}_${side}`] : player[`${market}_${b}`];
    if (odds !== null && odds !== undefined) {
      allOddsForThisLine.push(odds);
    }
  });

  if (allOddsForThisLine.length < 2) return 0;

  const bestOdds = Math.max(...allOddsForThisLine);
  const bestOddsCount = allOddsForThisLine.filter(o => o === bestOdds).length;

  // Only calculate edge if this book has the unique best odds
  if (bookOdds !== bestOdds || bestOddsCount !== 1) return 0;

  const secondBestOdds = Math.max(...allOddsForThisLine.filter(o => o !== bestOdds));
  if (secondBestOdds === -Infinity) return 0;

  // Handle cross-zero comparison (positive best odds vs negative second best)
  if (bestOdds > 0 && secondBestOdds < 0) {
    const spreadPositive = bestOdds - 100;  // Distance from +100
    const spreadNegative = secondBestOdds - (-100);  // Distance from -100 (will be negative)
    const totalSpread = spreadPositive - spreadNegative;  // Total spread through baseline
    return Math.abs(totalSpread / secondBestOdds);
  }

  return (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
};

/**
 * Get edge for a player/book/market combination
 * Always recalculates client-side for accuracy
 * Binary markets: no side (direct field name)
 * Numeric markets: 'over' side
 */
export const getEdgeForPlayerBook = (player, book, market, bookmakers) => {
  // Determine if this is a binary market (no side suffix) or numeric market (needs 'over')
  const side = isBinaryMarket(market) ? null : 'over';

  // Always recalculate for consistency
  return recalculateEdge(player, book, market, bookmakers, side);
};

/**
 * Helper function to determine period label and key from market string
 * Supports football quarters (q1-q4), halves (h1-h2), and hockey periods (p1-p3)
 */
const determinePeriod = (marketString) => {
  // Football quarters
  if (marketString.includes('_q1')) return { period: 'Q1', periodKey: 'q1' };
  if (marketString.includes('_q2')) return { period: 'Q2', periodKey: 'q2' };
  if (marketString.includes('_q3')) return { period: 'Q3', periodKey: 'q3' };
  if (marketString.includes('_q4')) return { period: 'Q4', periodKey: 'q4' };

  // Football/Basketball halves
  if (marketString.includes('_h1')) return { period: '1st Half', periodKey: 'h1' };
  if (marketString.includes('_h2')) return { period: '2nd Half', periodKey: 'h2' };

  // Hockey periods
  if (marketString.includes('_p1')) return { period: '1st', periodKey: 'p1' };
  if (marketString.includes('_p2')) return { period: '2nd', periodKey: 'p2' };
  if (marketString.includes('_p3')) return { period: '3rd', periodKey: 'p3' };

  // Default to game
  return { period: 'Game', periodKey: 'game' };
};

export const transformMarketData = (marketData, selectedMarket) => {
  const playerEntries = [];
  const seenKeys = new Set(); // Track unique keys to prevent duplicates
  
  // Check if this is NCAAF data
  const isNCAAF = marketData.sport === 'ncaaf';
  
  // Check for new structure (version: "new-structure")
  if (marketData.version === 'new-structure' && marketData.markets && marketData.markets.length > 0) {
    const marketRecord = marketData.markets[0]; // Usually just one record per game
    const awayTeam = marketRecord.awayTeam;
    const homeTeam = marketRecord.homeTeam;
    
    
    // Handle h2h-related markets - use unified processing for all h2h markets
    // This includes: h2h (Game), h2h_quarters, h2h_halves, h2h_all
    if ((selectedMarket === 'h2h' || selectedMarket === 'h2h_quarters' || selectedMarket === 'h2h_halves' || selectedMarket === 'h2h_all') && marketRecord.h2h) {
      marketRecord.h2h.forEach(h2hMarket => {
        // Only process standard h2h markets, skip alternates/3-way
        if (h2hMarket.market && h2hMarket.market !== 'h2h_standard') return;

        if (h2hMarket.books) {
          // Check what keys are in the first book to determine format
          const firstBook = Object.values(h2hMarket.books)[0];
          if (firstBook) {
            const bookKeys = Object.keys(firstBook);

            // Check if this market has a draw option (3-way market) - skip the entire market
            const hasDraw = bookKeys.some(key => key.toLowerCase().trim() === 'draw');
            if (hasDraw) return;

            // Determine if this is NFL format (home/away keys) or NCAAF format (team names)
            if (bookKeys.includes('home') && bookKeys.includes('away')) {
              // NFL format - use home/away keys directly
              ['away', 'home'].forEach((teamKey) => {
                const teamEntry = {
                  name: teamKey === 'away' ? awayTeam : homeTeam,
                  team: teamKey,
                  playerId: `team_${teamKey}`,
                  teamId: teamKey,
                  point: null,
                  tierKey: `team_${teamKey}_h2h`,
                  starter: false,
                  positionCode: null,
                  position: null,
                  sortOrder: teamKey === 'away' ? 0 : teamKey === 'draw' ? 0.5 : 1
                };
                
                // Add odds for each bookmaker
                const bookPrices = [];
                Object.entries(h2hMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamKey];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    teamEntry[`h2h_${displayBook}`] = oddsValue;
                    bookPrices.push({ book: displayBook, price: oddsValue });
                  }
                });
                
                // Calculate best book price and diff
                if (bookPrices.length > 0) {
                  const bestOdds = bookPrices.reduce((best, current) => 
                    current.price > best.price ? current : best
                  );
                  teamEntry['h2h_bestBookPrice'] = bestOdds.price;
                  
                  const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                  if (otherPrices.length > 0) {
                    const secondBest = Math.max(...otherPrices);
                    if (bestOdds.price > 0 && secondBest > 0) {
                      teamEntry['h2h_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
                    } else if (bestOdds.price < 0 && secondBest < 0) {
                      teamEntry['h2h_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
                    }
                  }
                }
                
                playerEntries.push(teamEntry);
              });
            } else {
              // NCAAF format - use actual team names as keys
              bookKeys.forEach((teamName) => {
                // Match team name to determine if it's away, home, or draw
                let teamKey = 'away'; // default

                // Normalize team names for comparison
                const normalizedTeamName = teamName.toLowerCase().trim();
                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                // Check if this is a draw option
                if (normalizedTeamName === 'draw') {
                  teamKey = 'draw';
                }
                // Check if this team matches the home team
                else if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }
                // Check if this team matches the away team
                else if (normalizedTeamName === normalizedAwayTeam ||
                         normalizedTeamName.includes(normalizedAwayTeam) ||
                         normalizedAwayTeam?.includes(normalizedTeamName)) {
                  teamKey = 'away';
                }

                const teamEntry = {
                  name: teamKey === 'away' ? awayTeam : teamKey === 'home' ? homeTeam : 'Draw',
                  team: teamKey,
                  playerId: `team_${teamKey}`,
                  teamId: teamKey,
                  point: null,
                  tierKey: `team_${teamKey}_h2h`,
                  starter: false,
                  positionCode: null,
                  position: null,
                  sortOrder: teamKey === 'away' ? 0 : teamKey === 'draw' ? 0.5 : 1
                };

                // Add odds for each bookmaker
                const bookPrices = [];
                Object.entries(h2hMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];  // Use actual team name as key
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    teamEntry[`h2h_${displayBook}`] = oddsValue;
                    bookPrices.push({ book: displayBook, price: oddsValue });
                  }
                });

                // Calculate best book price and diff
                if (bookPrices.length > 0) {
                  const bestOdds = bookPrices.reduce((best, current) =>
                    current.price > best.price ? current : best
                  );
                  teamEntry['h2h_bestBookPrice'] = bestOdds.price;

                  const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                  if (otherPrices.length > 0) {
                    const secondBest = Math.max(...otherPrices);
                    if (bestOdds.price > 0 && secondBest > 0) {
                      teamEntry['h2h_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
                    } else if (bestOdds.price < 0 && secondBest < 0) {
                      teamEntry['h2h_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
                    }
                  }
                }

                playerEntries.push(teamEntry);
              });
            }
          }
        }
      });
      
      // Also include totals if they exist
      if (marketRecord.totals) {
        marketRecord.totals.forEach(totalsMarket => {
          const point = totalsMarket.point;
          
          ['over', 'under'].forEach((betType, betIndex) => {
            const tierKey = `totals_${point}_${betType}`;
            const playerEntry = {
              name: betType.toUpperCase(),
              team: null,
              playerId: `totals_${point}_${betType}`,
              teamId: null,
              point: point,
              betType: betType,
              betOrder: betIndex,
              tierKey: tierKey,
              starter: false,
              positionCode: null,
              position: null,
              sortOrder: 100 + (point * 2) + betIndex
            };
            
            // Add odds for each bookmaker
            const bookPrices = [];
            Object.entries(totalsMarket.books).forEach(([bookName, bookOdds]) => {
              if (bookOdds && bookOdds[betType] !== undefined && bookOdds[betType] !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                playerEntry[`totals_${displayBook}`] = bookOdds[betType];
                bookPrices.push({ book: displayBook, price: bookOdds[betType] });
              }
            });
            
            // Calculate best book price and diff
            if (bookPrices.length > 0) {
              const bestOdds = bookPrices.reduce((best, current) => 
                current.price > best.price ? current : best
              );
              playerEntry['totals_bestBookPrice'] = bestOdds.price;
              
              const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
              if (otherPrices.length > 0) {
                const secondBest = Math.max(...otherPrices);
                if (bestOdds.price > 0 && secondBest > 0) {
                  playerEntry['totals_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
                } else if (bestOdds.price < 0 && secondBest < 0) {
                  playerEntry['totals_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
                }
              }
            }
            
            playerEntries.push(playerEntry);
          });
        });
      }
      
      // Also include spreads if they exist
      if (marketRecord.spreads) {
        marketRecord.spreads.forEach(spreadsMarket => {
          const point = spreadsMarket.point;
          
          if (spreadsMarket.books) {
            const firstBook = Object.values(spreadsMarket.books)[0];
            if (firstBook) {
              const bookKeys = Object.keys(firstBook);
              const teamName = bookKeys[0];
              
              // Determine team key based on whether it's NFL format (home/away) or NCAAF format (team names)
              let teamKey;
              if (teamName === 'home' || teamName === 'away') {
                // NFL format
                teamKey = teamName;
              } else {
                // NCAAF format - match team name to determine if it's away or home
                teamKey = 'away'; // default

                // Normalize team names for comparison
                const normalizedTeamName = teamName.toLowerCase().trim();
                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                // Check if this team matches the home team
                if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }
                // Check if this team matches the away team
                else if (normalizedTeamName === normalizedAwayTeam ||
                         normalizedTeamName.includes(normalizedAwayTeam) ||
                         normalizedAwayTeam?.includes(normalizedTeamName)) {
                  teamKey = 'away';
                }
              }
              
              const playerEntry = {
                name: `${point > 0 ? '+' : ''}${point}`,
                team: teamKey,
                playerId: `spreads_${point}_${teamKey}`,
                teamId: teamKey,
                point: point,
                betType: teamKey,
                betOrder: teamKey === 'away' ? 0 : 1,
                tierKey: `spreads_${point}_${teamKey}`,
                starter: false,
                positionCode: null,
                position: null,
                sortOrder: teamKey === 'away' ? 200 : 201
              };
              
              // Add odds for each bookmaker
              const bookPrices = [];
              Object.entries(spreadsMarket.books).forEach(([bookName, bookData]) => {
                const oddsValue = bookData[teamName];  // Use teamName (the actual key from data)
                if (oddsValue !== undefined && oddsValue !== null) {
                  const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                  playerEntry[`spreads_${displayBook}`] = oddsValue;
                  bookPrices.push({ book: displayBook, price: oddsValue });
                }
              });
              
              // Calculate best book price and diff
              if (bookPrices.length > 0) {
                const bestOdds = bookPrices.reduce((best, current) => 
                  current.price > best.price ? current : best
                );
                playerEntry['spreads_bestBookPrice'] = bestOdds.price;
                
                const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                if (otherPrices.length > 0) {
                  const secondBest = Math.max(...otherPrices);
                  if (bestOdds.price > 0 && secondBest > 0) {
                    playerEntry['spreads_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
                  } else if (bestOdds.price < 0 && secondBest < 0) {
                    playerEntry['spreads_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
                  }
                }
              }
              
              playerEntries.push(playerEntry);
            }
          }
        });
      }

      // Also process quarters h2h from h2h_quarters array
      if (marketRecord.h2h_quarters) {
        marketRecord.h2h_quarters.forEach(h2hMarket => {
          const market = h2hMarket.market;

          // Only process standard quarter markets (h2h_quarters_q1, h2h_quarters_q2, etc.)
          // Skip 3-way markets and alternates
          if (market && (market.includes('3_way') || market.includes('3way') || market.includes('alt'))) return;

          // Determine which quarter
          let quarter = null;
          if (market.includes('_q1')) quarter = 'q1';
          else if (market.includes('_q2')) quarter = 'q2';
          else if (market.includes('_q3')) quarter = 'q3';
          else if (market.includes('_q4')) quarter = 'q4';

          if (!quarter) return; // Skip non-quarter markets

          if (h2hMarket.books) {
            const firstBook = Object.values(h2hMarket.books)[0];
            if (firstBook) {
              const bookKeys = Object.keys(firstBook);

              // Check if this market has a draw option (3-way market) - skip the entire market
              const hasDraw = bookKeys.some(key => key.toLowerCase().trim() === 'draw');
              if (hasDraw) return;

              bookKeys.forEach((teamName) => {
                const normalizedTeamName = teamName.toLowerCase().trim();

                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this team matches the home team
                if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }
                // Check if this team matches the away team
                else if (normalizedTeamName === normalizedAwayTeam ||
                         normalizedTeamName.includes(normalizedAwayTeam) ||
                         normalizedAwayTeam?.includes(normalizedTeamName)) {
                  teamKey = 'away';
                }

                const playerId = `${quarter}_${teamKey}`;
                let teamEntry = playerEntries.find(p => p.playerId === playerId);

                if (!teamEntry) {
                  teamEntry = {
                    team: teamKey,
                    playerId: playerId,
                    teamId: teamKey,
                    point: null,
                    period: quarter.toUpperCase(),
                    tierKey: `${quarter}_${teamKey}_h2h`,
                    starter: false,
                    positionCode: null,
                    position: null,
                    sortOrder: teamKey === 'away' ? 0 : teamKey === 'draw' ? 0.5 : 1
                  };
                  playerEntries.push(teamEntry);
                }

                const bookPrices = [];
                Object.entries(h2hMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    teamEntry[`h2h_${displayBook}`] = oddsValue;
                    bookPrices.push({ book: displayBook, price: oddsValue });
                  }
                });

                // Calculate best book price and diff for quarters
                if (bookPrices.length > 0) {
                  const bestOdds = bookPrices.reduce((best, current) =>
                    current.price > best.price ? current : best
                  );
                  teamEntry['h2h_bestBookPrice'] = bestOdds.price;

                  const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                  if (otherPrices.length > 0) {
                    const secondBest = Math.max(...otherPrices);
                    if (bestOdds.price > 0 && secondBest > 0) {
                      teamEntry['h2h_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
                    } else if (bestOdds.price < 0 && secondBest < 0) {
                      teamEntry['h2h_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
                    }
                  }
                }
              });
            }
          }
        });
      }

      // Also process halves h2h from h2h_halves array
      if (marketRecord.h2h_halves) {
        marketRecord.h2h_halves.forEach(h2hMarket => {
          const market = h2hMarket.market;

          // Only process standard halves markets, skip 3-way and alternates
          if (market && (market.includes('3_way') || market.includes('3way') || market.includes('alt'))) return;

          // Determine which half
          let half = null;
          if (market.includes('_h1')) half = 'h1';
          else if (market.includes('_h2')) half = 'h2';

          if (!half) return; // Skip non-half markets

          if (h2hMarket.books) {
            const firstBook = Object.values(h2hMarket.books)[0];
            if (firstBook) {
              const bookKeys = Object.keys(firstBook);

              // Check if this market has a draw option (3-way market) - skip the entire market
              const hasDraw = bookKeys.some(key => key.toLowerCase().trim() === 'draw');
              if (hasDraw) return;

              bookKeys.forEach((teamName) => {
                const normalizedTeamName = teamName.toLowerCase().trim();

                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this team matches the home team
                if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }
                // Check if this team matches the away team
                else if (normalizedTeamName === normalizedAwayTeam ||
                         normalizedTeamName.includes(normalizedAwayTeam) ||
                         normalizedAwayTeam?.includes(normalizedTeamName)) {
                  teamKey = 'away';
                }

                const playerId = `${half}_${teamKey}`;
                let teamEntry = playerEntries.find(p => p.playerId === playerId);

                if (!teamEntry) {
                  teamEntry = {
                    team: teamKey,
                    playerId: playerId,
                    teamId: teamKey,
                    point: null,
                    period: half === 'h1' ? '1st Half' : '2nd Half',
                    tierKey: `${half}_${teamKey}_h2h`,
                    starter: false,
                    positionCode: null,
                    position: null,
                    sortOrder: teamKey === 'away' ? 0 : teamKey === 'draw' ? 0.5 : 1
                  };
                  playerEntries.push(teamEntry);
                }

                const bookPrices = [];
                Object.entries(h2hMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    teamEntry[`h2h_${displayBook}`] = oddsValue;
                    bookPrices.push({ book: displayBook, price: oddsValue });
                  }
                });

                // Calculate best book price and diff for halves
                if (bookPrices.length > 0) {
                  const bestOdds = bookPrices.reduce((best, current) =>
                    current.price > best.price ? current : best
                  );
                  teamEntry['h2h_bestBookPrice'] = bestOdds.price;

                  const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                  if (otherPrices.length > 0) {
                    const secondBest = Math.max(...otherPrices);
                    if (bestOdds.price > 0 && secondBest > 0) {
                      teamEntry['h2h_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
                    } else if (bestOdds.price < 0 && secondBest < 0) {
                      teamEntry['h2h_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
                    }
                  }
                }
              });
            }
          }
        });
      }

      // Process quarters totals from totals_quarters array
      if (marketRecord.totals_quarters) {
        marketRecord.totals_quarters.forEach(totalsMarket => {
          const market = totalsMarket.market;
          const point = totalsMarket.point;

          // Determine which quarter
          let quarter = null;
          if (market.includes('_q1')) quarter = 'q1';
          else if (market.includes('_q2')) quarter = 'q2';
          else if (market.includes('_q3')) quarter = 'q3';
          else if (market.includes('_q4')) quarter = 'q4';

          if (!quarter) return;

          if (totalsMarket.books) {
            // Process Over
            const overPlayerId = `totals_${quarter}_${point}_over`;
            let overEntry = playerEntries.find(p => p.playerId === overPlayerId);

            if (!overEntry) {
              overEntry = {
                name: 'Over',
                playerId: overPlayerId,
                point: point,
                period: quarter.toUpperCase(),
                betType: 'over',
                tierKey: `totals_${quarter}_${point}_over`
              };
              playerEntries.push(overEntry);
            }

            Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
              if (bookData.over !== undefined && bookData.over !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                overEntry[`totals_${displayBook}`] = bookData.over;
              }
            });

            // Process Under
            const underPlayerId = `totals_${quarter}_${point}_under`;
            let underEntry = playerEntries.find(p => p.playerId === underPlayerId);

            if (!underEntry) {
              underEntry = {
                name: 'Under',
                playerId: underPlayerId,
                point: point,
                period: quarter.toUpperCase(),
                betType: 'under',
                tierKey: `totals_${quarter}_${point}_under`
              };
              playerEntries.push(underEntry);
            }

            Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
              if (bookData.under !== undefined && bookData.under !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                underEntry[`totals_${displayBook}`] = bookData.under;
              }
            });
          }
        });
      }

      // Process halves totals from totals_halves array
      if (marketRecord.totals_halves) {
        marketRecord.totals_halves.forEach(totalsMarket => {
          const market = totalsMarket.market;
          const point = totalsMarket.point;

          // Determine which half
          let half = null;
          if (market.includes('_h1')) half = 'h1';
          else if (market.includes('_h2')) half = 'h2';

          if (!half) return;

          if (totalsMarket.books) {
            // Process Over
            const overPlayerId = `totals_${half}_${point}_over`;
            let overEntry = playerEntries.find(p => p.playerId === overPlayerId);

            if (!overEntry) {
              overEntry = {
                name: 'Over',
                playerId: overPlayerId,
                point: point,
                period: half === 'h1' ? '1st Half' : '2nd Half',
                betType: 'over',
                tierKey: `totals_${half}_${point}_over`
              };
              playerEntries.push(overEntry);
            }

            Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
              if (bookData.over !== undefined && bookData.over !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                overEntry[`totals_${displayBook}`] = bookData.over;
              }
            });

            // Process Under
            const underPlayerId = `totals_${half}_${point}_under`;
            let underEntry = playerEntries.find(p => p.playerId === underPlayerId);

            if (!underEntry) {
              underEntry = {
                name: 'Under',
                playerId: underPlayerId,
                point: point,
                period: half === 'h1' ? '1st Half' : '2nd Half',
                betType: 'under',
                tierKey: `totals_${half}_${point}_under`
              };
              playerEntries.push(underEntry);
            }

            Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
              if (bookData.under !== undefined && bookData.under !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                underEntry[`totals_${displayBook}`] = bookData.under;
              }
            });
          }
        });
      }

      // Process quarters spreads from spreads_quarters array
      if (marketRecord.spreads_quarters) {
        marketRecord.spreads_quarters.forEach(spreadsMarket => {
          const market = spreadsMarket.market;
          const point = spreadsMarket.point;

          // Determine which quarter
          let quarter = null;
          if (market.includes('_q1')) quarter = 'q1';
          else if (market.includes('_q2')) quarter = 'q2';
          else if (market.includes('_q3')) quarter = 'q3';
          else if (market.includes('_q4')) quarter = 'q4';

          if (!quarter) return;

          if (spreadsMarket.books) {
            const firstBook = Object.values(spreadsMarket.books)[0];
            if (firstBook) {
              const teamNames = Object.keys(firstBook);

              teamNames.forEach(teamName => {
                const normalizedTeamName = teamName.toLowerCase().trim();
                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this is a draw option
                if (normalizedTeamName === 'draw') {
                  teamKey = 'draw';
                }
                // Check if this team matches the home team
                else if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }

                const playerId = `spreads_${quarter}_${point}_${teamKey}`;
                let spreadEntry = playerEntries.find(p => p.playerId === playerId);

                if (!spreadEntry) {
                  spreadEntry = {
                    name: teamKey === 'away' ? awayTeam : homeTeam,
                    team: teamKey,
                    playerId: playerId,
                    point: point,
                    period: quarter.toUpperCase(),
                    betType: 'spread',
                    tierKey: `spreads_${quarter}_${point}_${teamKey}`
                  };
                  playerEntries.push(spreadEntry);
                }

                Object.entries(spreadsMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    spreadEntry[`spreads_${displayBook}`] = oddsValue;
                  }
                });
              });
            }
          }
        });
      }

      // Process halves spreads from spreads_halves array
      if (marketRecord.spreads_halves) {
        marketRecord.spreads_halves.forEach(spreadsMarket => {
          const market = spreadsMarket.market;
          const point = spreadsMarket.point;

          // Determine which half
          let half = null;
          if (market.includes('_h1')) half = 'h1';
          else if (market.includes('_h2')) half = 'h2';

          if (!half) return;

          if (spreadsMarket.books) {
            const firstBook = Object.values(spreadsMarket.books)[0];
            if (firstBook) {
              const teamNames = Object.keys(firstBook);

              teamNames.forEach(teamName => {
                const normalizedTeamName = teamName.toLowerCase().trim();
                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this is a draw option
                if (normalizedTeamName === 'draw') {
                  teamKey = 'draw';
                }
                // Check if this team matches the home team
                else if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }

                const playerId = `spreads_${half}_${point}_${teamKey}`;
                let spreadEntry = playerEntries.find(p => p.playerId === playerId);

                if (!spreadEntry) {
                  spreadEntry = {
                    name: teamKey === 'away' ? awayTeam : homeTeam,
                    team: teamKey,
                    playerId: playerId,
                    point: point,
                    period: half === 'h1' ? '1st Half' : '2nd Half',
                    betType: 'spread',
                    tierKey: `spreads_${half}_${point}_${teamKey}`
                  };
                  playerEntries.push(spreadEntry);
                }

                Object.entries(spreadsMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    spreadEntry[`spreads_${displayBook}`] = oddsValue;
                  }
                });
              });
            }
          }
        });
      }
    }


    // Handle h2h_periods market (for hockey)
    else if (selectedMarket === 'h2h_periods' && marketRecord.h2h) {
      // Group markets by period
      const periodGroups = { p1: [], p2: [], p3: [] };

      marketRecord.h2h.forEach(h2hMarket => {
        const market = h2hMarket.market;
        if (market.includes('_p1')) periodGroups.p1.push({ type: 'h2h', data: h2hMarket });
        else if (market.includes('_p2')) periodGroups.p2.push({ type: 'h2h', data: h2hMarket });
        else if (market.includes('_p3')) periodGroups.p3.push({ type: 'h2h', data: h2hMarket });
      });

      // Process each period
      Object.entries(periodGroups).forEach(([periodKey, markets]) => {
        markets.forEach(marketItem => {
          const h2hMarket = marketItem.data;
          if (h2hMarket.books) {
            const firstBook = Object.values(h2hMarket.books)[0];
            if (firstBook) {
              const bookKeys = Object.keys(firstBook);

              bookKeys.forEach((teamName) => {
                const normalizedTeamName = teamName.toLowerCase().trim();

                // Skip draw entries - we don't support 3-way markets yet
                if (normalizedTeamName === 'draw') return;

                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this team matches the home team
                if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }

                const periodLabel = periodKey === 'p1' ? '1st' : periodKey === 'p2' ? '2nd' : '3rd';
                const teamEntry = {
                  name: teamKey === 'away' ? awayTeam : homeTeam,
                  team: teamKey,
                  playerId: `${periodKey}_${teamKey}`,
                  teamId: teamKey,
                  point: null,
                  period: periodLabel,
                  tierKey: `${periodKey}_${teamKey}_h2h`,
                  starter: false,
                  positionCode: null,
                  position: null,
                  sortOrder: teamKey === 'away' ? 0 : teamKey === 'draw' ? 0.5 : 1
                };

                // Add odds for each bookmaker
                Object.entries(h2hMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    teamEntry[`h2h_periods_${displayBook}`] = oddsValue;
                  }
                });

                // Also process spreads and totals for periods if available
                if (marketRecord.spreads) {
                  marketRecord.spreads.forEach(spreadsMarket => {
                    const market = spreadsMarket.market;
                    if (market.includes(`_${periodKey}`)) {
                      if (spreadsMarket.books) {
                        Object.entries(spreadsMarket.books).forEach(([bookName, bookData]) => {
                          Object.keys(bookData).forEach(spreadTeamName => {
                            const normalizedSpreadTeam = spreadTeamName.toLowerCase().trim();
                            if ((teamKey === 'away' && (normalizedSpreadTeam === normalizedAwayTeam || normalizedAwayTeam?.includes(normalizedSpreadTeam))) ||
                                (teamKey === 'home' && (normalizedSpreadTeam === normalizedHomeTeam || normalizedHomeTeam?.includes(normalizedSpreadTeam)))) {
                              const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                              teamEntry[`spreads_periods_${displayBook}`] = bookData[spreadTeamName];
                              teamEntry.point = spreadsMarket.point;
                            }
                          });
                        });
                      }
                    }
                  });
                }

                if (marketRecord.totals) {
                  marketRecord.totals.forEach(totalsMarket => {
                    const market = totalsMarket.market;
                    if (market.includes(`_${periodKey}`)) {
                      if (totalsMarket.books) {
                        Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
                          const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                          if (bookData.over !== undefined) {
                            teamEntry[`totals_periods_${displayBook}`] = bookData.over;
                          }
                        });
                      }
                    }
                  });
                }

                playerEntries.push(teamEntry);
              });
            }
          }
        });
      });
    }

    // Handle spreads market directly
    else if (selectedMarket === 'spreads' && marketRecord.spreads) {
      marketRecord.spreads.forEach(spreadsMarket => {
        const point = spreadsMarket.point;
        
        if (spreadsMarket.books) {
          const firstBook = Object.values(spreadsMarket.books)[0];
          if (firstBook) {
            const bookKeys = Object.keys(firstBook);
            const teamName = bookKeys[0];
            
            // Determine team key based on whether it's NFL format (home/away) or NCAAF format (team names)
            let teamKey;
            if (teamName === 'home' || teamName === 'away') {
              // NFL format
              teamKey = teamName;
            } else {
              // NCAAF format - match team name to determine if it's away or home
              teamKey = 'away'; // default

              // Normalize team names for comparison
              const normalizedTeamName = teamName.toLowerCase().trim();
              const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
              const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

              // Check if this team matches the home team
              if (normalizedTeamName === normalizedHomeTeam ||
                  normalizedTeamName.includes(normalizedHomeTeam) ||
                  normalizedHomeTeam?.includes(normalizedTeamName)) {
                teamKey = 'home';
              }
              // Check if this team matches the away team
              else if (normalizedTeamName === normalizedAwayTeam ||
                       normalizedTeamName.includes(normalizedAwayTeam) ||
                       normalizedAwayTeam?.includes(normalizedTeamName)) {
                teamKey = 'away';
              }
            }
            
            const playerEntry = {
              name: `${point > 0 ? '+' : ''}${point}`,
              team: teamKey,
              playerId: `spreads_${point}_${teamKey}`,
              teamId: teamKey,
              point: point,
              betType: teamKey,
              betOrder: teamKey === 'away' ? 0 : 1,
              tierKey: `spreads_${point}_${teamKey}`,
              starter: false,
              positionCode: null,
              position: null,
              sortOrder: teamKey === 'away' ? 200 : 201
            };
            
            // Add odds for each bookmaker
            const bookPrices = [];
            Object.entries(spreadsMarket.books).forEach(([bookName, bookData]) => {
              const oddsValue = bookData[teamName];  // Use teamName (the actual key from data)
              if (oddsValue !== undefined && oddsValue !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                playerEntry[`spreads_${displayBook}`] = oddsValue;
                bookPrices.push({ book: displayBook, price: oddsValue });
              }
            });
            
            // Calculate best book price and diff
            if (bookPrices.length > 0) {
              const bestOdds = bookPrices.reduce((best, current) => 
                current.price > best.price ? current : best
              );
              playerEntry['spreads_bestBookPrice'] = bestOdds.price;
              
              const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
              if (otherPrices.length > 0) {
                const secondBest = Math.max(...otherPrices);
                if (bestOdds.price > 0 && secondBest > 0) {
                  playerEntry['spreads_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
                } else if (bestOdds.price < 0 && secondBest < 0) {
                  playerEntry['spreads_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
                }
              }
            }
            
            playerEntries.push(playerEntry);
          }
        }
      });
    }
    
    // Handle totals market directly
    else if (selectedMarket === 'totals' && marketRecord.totals) {
      marketRecord.totals.forEach(totalsMarket => {
        const point = totalsMarket.point;
        
        ['over', 'under'].forEach((betType, betIndex) => {
          const tierKey = `totals_${point}_${betType}`;
          const playerEntry = {
            name: betType.toUpperCase(),
            team: null,
            playerId: `totals_${point}_${betType}`,
            teamId: null,
            point: point,
            betType: betType,
            betOrder: betIndex,
            tierKey: tierKey,
            starter: false,
            positionCode: null,
            position: null,
            sortOrder: 100 + (point * 2) + betIndex
          };
          
          // Add odds for each bookmaker
          const bookPrices = [];
          Object.entries(totalsMarket.books).forEach(([bookName, bookOdds]) => {
            if (bookOdds && bookOdds[betType] !== undefined && bookOdds[betType] !== null) {
              const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
              playerEntry[`totals_${displayBook}`] = bookOdds[betType];
              bookPrices.push({ book: displayBook, price: bookOdds[betType] });
            }
          });
          
          // Calculate best book price and diff
          if (bookPrices.length > 0) {
            const bestOdds = bookPrices.reduce((best, current) => 
              current.price > best.price ? current : best
            );
            playerEntry['totals_bestBookPrice'] = bestOdds.price;
            
            const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
            if (otherPrices.length > 0) {
              const secondBest = Math.max(...otherPrices);
              if (bestOdds.price > 0 && secondBest > 0) {
                playerEntry['totals_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
              } else if (bestOdds.price < 0 && secondBest < 0) {
                playerEntry['totals_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
              }
            }
          }
          
          playerEntries.push(playerEntry);
        });
      });
    }

    // This block should NOT run anymore since we handle everything above
    // But keeping it as a fallback for now
    else if (false && selectedMarket === 'h2h_all') {
      // Process main h2h (game level)
      if (marketRecord.h2h) {
        marketRecord.h2h.forEach(h2hMarket => {
          const market = h2hMarket.market;

          if (h2hMarket.books) {
            const firstBook = Object.values(h2hMarket.books)[0];
            if (firstBook) {
              const bookKeys = Object.keys(firstBook);

              bookKeys.forEach((teamName) => {
                const normalizedTeamName = teamName.toLowerCase().trim();

                // Skip draw entries - we don't support 3-way markets yet
                if (normalizedTeamName === 'draw') return;

                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this team matches the home team
                if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }

                const playerId = `game_${teamKey}`;
                let teamEntry = playerEntries.find(p => p.playerId === playerId);

                if (!teamEntry) {
                  teamEntry = {
                    team: teamKey,
                    playerId: playerId,
                    teamId: teamKey,
                    point: null,
                    period: 'Game',
                    tierKey: `game_${teamKey}_h2h`,
                    starter: false,
                    positionCode: null,
                    position: null,
                    sortOrder: teamKey === 'away' ? 0 : teamKey === 'draw' ? 0.5 : 1
                  };
                  playerEntries.push(teamEntry);
                }

                Object.entries(h2hMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    teamEntry[`h2h_${displayBook}`] = oddsValue;
                  }
                });
              });
            }
          }
        });
      }

      // Process quarters h2h from h2h_quarters array
      if (marketRecord.h2h_quarters) {
        marketRecord.h2h_quarters.forEach(h2hMarket => {
          const market = h2hMarket.market;

          // Determine which quarter
          let quarter = null;
          if (market.includes('_q1')) quarter = 'q1';
          else if (market.includes('_q2')) quarter = 'q2';
          else if (market.includes('_q3')) quarter = 'q3';
          else if (market.includes('_q4')) quarter = 'q4';

          if (!quarter) return; // Skip non-quarter markets

          if (h2hMarket.books) {
            const firstBook = Object.values(h2hMarket.books)[0];
            if (firstBook) {
              const bookKeys = Object.keys(firstBook);

              bookKeys.forEach((teamName) => {
                const normalizedTeamName = teamName.toLowerCase().trim();

                // Skip draw entries - we don't support 3-way markets yet
                if (normalizedTeamName === 'draw') return;

                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this team matches the home team
                if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }
                // Check if this team matches the away team
                else if (normalizedTeamName === normalizedAwayTeam ||
                         normalizedTeamName.includes(normalizedAwayTeam) ||
                         normalizedAwayTeam?.includes(normalizedTeamName)) {
                  teamKey = 'away';
                }

                const playerId = `${quarter}_${teamKey}`;
                let teamEntry = playerEntries.find(p => p.playerId === playerId);

                if (!teamEntry) {
                  teamEntry = {
                    team: teamKey,
                    playerId: playerId,
                    teamId: teamKey,
                    point: null,
                    period: quarter.toUpperCase(),
                    tierKey: `${quarter}_${teamKey}_h2h`,
                    starter: false,
                    positionCode: null,
                    position: null,
                    sortOrder: teamKey === 'away' ? 0 : teamKey === 'draw' ? 0.5 : 1
                  };
                  playerEntries.push(teamEntry);
                }

                Object.entries(h2hMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    teamEntry[`h2h_${displayBook}`] = oddsValue;
                  }
                });
              });
            }
          }
        });
      }

      // Process halves h2h from h2h_halves array
      if (marketRecord.h2h_halves) {
        marketRecord.h2h_halves.forEach(h2hMarket => {
          const market = h2hMarket.market;

          // Only process standard halves markets, skip 3-way and alternates
          if (market && (market.includes('3_way') || market.includes('3way') || market.includes('alt'))) return;

          // Determine which half
          let half = null;
          if (market.includes('_h1')) half = 'h1';
          else if (market.includes('_h2')) half = 'h2';

          if (!half) return; // Skip non-half markets

          if (h2hMarket.books) {
            const firstBook = Object.values(h2hMarket.books)[0];
            if (firstBook) {
              const bookKeys = Object.keys(firstBook);

              // Check if this market has a draw option (3-way market) - skip the entire market
              const hasDraw = bookKeys.some(key => key.toLowerCase().trim() === 'draw');
              if (hasDraw) return;

              bookKeys.forEach((teamName) => {
                const normalizedTeamName = teamName.toLowerCase().trim();

                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this team matches the home team
                if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }
                // Check if this team matches the away team
                else if (normalizedTeamName === normalizedAwayTeam ||
                         normalizedTeamName.includes(normalizedAwayTeam) ||
                         normalizedAwayTeam?.includes(normalizedTeamName)) {
                  teamKey = 'away';
                }

                const playerId = `${half}_${teamKey}`;
                let teamEntry = playerEntries.find(p => p.playerId === playerId);

                if (!teamEntry) {
                  teamEntry = {
                    team: teamKey,
                    playerId: playerId,
                    teamId: teamKey,
                    point: null,
                    period: half === 'h1' ? '1st Half' : '2nd Half',
                    tierKey: `${half}_${teamKey}_h2h`,
                    starter: false,
                    positionCode: null,
                    position: null,
                    sortOrder: teamKey === 'away' ? 0 : teamKey === 'draw' ? 0.5 : 1
                  };
                  playerEntries.push(teamEntry);
                }

                Object.entries(h2hMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    teamEntry[`h2h_${displayBook}`] = oddsValue;
                  }
                });
              });
            }
          }
        });
      }

      // Process main game totals
      if (marketRecord.totals) {
        marketRecord.totals.forEach(totalsMarket => {
          const market = totalsMarket.market;
          const point = totalsMarket.point;

          // Only process if it's a main game market (not quarters/halves)
          if (market.includes('_q') || market.includes('_h') || market.includes('_p')) return;

          if (totalsMarket.books) {
            // Process Over
            const overPlayerId = `totals_game_${point}_over`;
            let overEntry = playerEntries.find(p => p.playerId === overPlayerId);

            if (!overEntry) {
              overEntry = {
                name: 'Over',
                playerId: overPlayerId,
                point: point,
                period: 'Game',
                betType: 'over',
                tierKey: `totals_game_${point}_over`
              };
              playerEntries.push(overEntry);
            }

            Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
              if (bookData.over !== undefined && bookData.over !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                overEntry[`totals_${displayBook}`] = bookData.over;
              }
            });

            // Process Under
            const underPlayerId = `totals_game_${point}_under`;
            let underEntry = playerEntries.find(p => p.playerId === underPlayerId);

            if (!underEntry) {
              underEntry = {
                name: 'Under',
                playerId: underPlayerId,
                point: point,
                period: 'Game',
                betType: 'under',
                tierKey: `totals_game_${point}_under`
              };
              playerEntries.push(underEntry);
            }

            Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
              if (bookData.under !== undefined && bookData.under !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                underEntry[`totals_${displayBook}`] = bookData.under;
              }
            });
          }
        });
      }

      // Process quarters totals from totals_quarters array
      if (marketRecord.totals_quarters) {
        marketRecord.totals_quarters.forEach(totalsMarket => {
          const market = totalsMarket.market;
          const point = totalsMarket.point;

          // Determine which quarter
          let quarter = null;
          if (market.includes('_q1')) quarter = 'q1';
          else if (market.includes('_q2')) quarter = 'q2';
          else if (market.includes('_q3')) quarter = 'q3';
          else if (market.includes('_q4')) quarter = 'q4';

          if (!quarter) return;

          if (totalsMarket.books) {
            // Process Over
            const overPlayerId = `totals_${quarter}_${point}_over`;
            let overEntry = playerEntries.find(p => p.playerId === overPlayerId);

            if (!overEntry) {
              overEntry = {
                name: 'Over',
                playerId: overPlayerId,
                point: point,
                period: quarter.toUpperCase(),
                betType: 'over',
                tierKey: `totals_${quarter}_${point}_over`
              };
              playerEntries.push(overEntry);
            }

            Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
              if (bookData.over !== undefined && bookData.over !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                overEntry[`totals_${displayBook}`] = bookData.over;
              }
            });

            // Process Under
            const underPlayerId = `totals_${quarter}_${point}_under`;
            let underEntry = playerEntries.find(p => p.playerId === underPlayerId);

            if (!underEntry) {
              underEntry = {
                name: 'Under',
                playerId: underPlayerId,
                point: point,
                period: quarter.toUpperCase(),
                betType: 'under',
                tierKey: `totals_${quarter}_${point}_under`
              };
              playerEntries.push(underEntry);
            }

            Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
              if (bookData.under !== undefined && bookData.under !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                underEntry[`totals_${displayBook}`] = bookData.under;
              }
            });
          }
        });
      }

      // Process halves totals from totals_halves array
      if (marketRecord.totals_halves) {
        marketRecord.totals_halves.forEach(totalsMarket => {
          const market = totalsMarket.market;
          const point = totalsMarket.point;

          // Determine which half
          let half = null;
          if (market.includes('_h1')) half = 'h1';
          else if (market.includes('_h2')) half = 'h2';

          if (!half) return;

          if (totalsMarket.books) {
            // Process Over
            const overPlayerId = `totals_${half}_${point}_over`;
            let overEntry = playerEntries.find(p => p.playerId === overPlayerId);

            if (!overEntry) {
              overEntry = {
                name: 'Over',
                playerId: overPlayerId,
                point: point,
                period: half === 'h1' ? '1st Half' : '2nd Half',
                betType: 'over',
                tierKey: `totals_${half}_${point}_over`
              };
              playerEntries.push(overEntry);
            }

            Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
              if (bookData.over !== undefined && bookData.over !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                overEntry[`totals_${displayBook}`] = bookData.over;
              }
            });

            // Process Under
            const underPlayerId = `totals_${half}_${point}_under`;
            let underEntry = playerEntries.find(p => p.playerId === underPlayerId);

            if (!underEntry) {
              underEntry = {
                name: 'Under',
                playerId: underPlayerId,
                point: point,
                period: half === 'h1' ? '1st Half' : '2nd Half',
                betType: 'under',
                tierKey: `totals_${half}_${point}_under`
              };
              playerEntries.push(underEntry);
            }

            Object.entries(totalsMarket.books).forEach(([bookName, bookData]) => {
              if (bookData.under !== undefined && bookData.under !== null) {
                const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                underEntry[`totals_${displayBook}`] = bookData.under;
              }
            });
          }
        });
      }

      // Process main game spreads
      if (marketRecord.spreads) {
        marketRecord.spreads.forEach(spreadsMarket => {
          const market = spreadsMarket.market;
          const point = spreadsMarket.point;

          // Only process if it's a main game market (not quarters/halves)
          if (market.includes('_q') || market.includes('_h') || market.includes('_p')) return;

          if (spreadsMarket.books) {
            const firstBook = Object.values(spreadsMarket.books)[0];
            if (firstBook) {
              const teamNames = Object.keys(firstBook);

              teamNames.forEach(teamName => {
                const normalizedTeamName = teamName.toLowerCase().trim();
                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this is a draw option
                if (normalizedTeamName === 'draw') {
                  teamKey = 'draw';
                }
                // Check if this team matches the home team
                else if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }

                const playerId = `spreads_game_${point}_${teamKey}`;
                let spreadEntry = playerEntries.find(p => p.playerId === playerId);

                if (!spreadEntry) {
                  spreadEntry = {
                    name: teamKey === 'away' ? awayTeam : homeTeam,
                    team: teamKey,
                    playerId: playerId,
                    point: point,
                    period: 'Game',
                    betType: 'spread',
                    tierKey: `spreads_game_${point}_${teamKey}`
                  };
                  playerEntries.push(spreadEntry);
                }

                Object.entries(spreadsMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    spreadEntry[`spreads_${displayBook}`] = oddsValue;
                  }
                });
              });
            }
          }
        });
      }

      // Process quarters spreads from spreads_quarters array
      if (marketRecord.spreads_quarters) {
        marketRecord.spreads_quarters.forEach(spreadsMarket => {
          const market = spreadsMarket.market;
          const point = spreadsMarket.point;

          // Determine which quarter
          let quarter = null;
          if (market.includes('_q1')) quarter = 'q1';
          else if (market.includes('_q2')) quarter = 'q2';
          else if (market.includes('_q3')) quarter = 'q3';
          else if (market.includes('_q4')) quarter = 'q4';

          if (!quarter) return;

          if (spreadsMarket.books) {
            const firstBook = Object.values(spreadsMarket.books)[0];
            if (firstBook) {
              const teamNames = Object.keys(firstBook);

              teamNames.forEach(teamName => {
                const normalizedTeamName = teamName.toLowerCase().trim();
                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this is a draw option
                if (normalizedTeamName === 'draw') {
                  teamKey = 'draw';
                }
                // Check if this team matches the home team
                else if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }

                const playerId = `spreads_${quarter}_${point}_${teamKey}`;
                let spreadEntry = playerEntries.find(p => p.playerId === playerId);

                if (!spreadEntry) {
                  spreadEntry = {
                    name: teamKey === 'away' ? awayTeam : homeTeam,
                    team: teamKey,
                    playerId: playerId,
                    point: point,
                    period: quarter.toUpperCase(),
                    betType: 'spread',
                    tierKey: `spreads_${quarter}_${point}_${teamKey}`
                  };
                  playerEntries.push(spreadEntry);
                }

                Object.entries(spreadsMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    spreadEntry[`spreads_${displayBook}`] = oddsValue;
                  }
                });
              });
            }
          }
        });
      }

      // Process halves spreads from spreads_halves array
      if (marketRecord.spreads_halves) {
        marketRecord.spreads_halves.forEach(spreadsMarket => {
          const market = spreadsMarket.market;
          const point = spreadsMarket.point;

          // Determine which half
          let half = null;
          if (market.includes('_h1')) half = 'h1';
          else if (market.includes('_h2')) half = 'h2';

          if (!half) return;

          if (spreadsMarket.books) {
            const firstBook = Object.values(spreadsMarket.books)[0];
            if (firstBook) {
              const teamNames = Object.keys(firstBook);

              teamNames.forEach(teamName => {
                const normalizedTeamName = teamName.toLowerCase().trim();
                const normalizedAwayTeam = awayTeam?.toLowerCase().trim();
                const normalizedHomeTeam = homeTeam?.toLowerCase().trim();

                let teamKey = 'away';
                // Check if this is a draw option
                if (normalizedTeamName === 'draw') {
                  teamKey = 'draw';
                }
                // Check if this team matches the home team
                else if (normalizedTeamName === normalizedHomeTeam ||
                    normalizedTeamName.includes(normalizedHomeTeam) ||
                    normalizedHomeTeam?.includes(normalizedTeamName)) {
                  teamKey = 'home';
                }

                const playerId = `spreads_${half}_${point}_${teamKey}`;
                let spreadEntry = playerEntries.find(p => p.playerId === playerId);

                if (!spreadEntry) {
                  spreadEntry = {
                    name: teamKey === 'away' ? awayTeam : homeTeam,
                    team: teamKey,
                    playerId: playerId,
                    point: point,
                    period: half === 'h1' ? '1st Half' : '2nd Half',
                    betType: 'spread',
                    tierKey: `spreads_${half}_${point}_${teamKey}`
                  };
                  playerEntries.push(spreadEntry);
                }

                Object.entries(spreadsMarket.books).forEach(([bookName, bookData]) => {
                  const oddsValue = bookData[teamName];
                  if (oddsValue !== undefined && oddsValue !== null) {
                    const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                    spreadEntry[`spreads_${displayBook}`] = oddsValue;
                  }
                });
              });
            }
          }
        });
      }
    }

    // Sort entries to ensure away team comes before home team for h2h markets
    return playerEntries.sort((a, b) => {
      // First sort by sortOrder if it exists
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }
      // Then by team (away before home)
      if (a.team && b.team) {
        if (a.team === 'away' && b.team === 'home') return -1;
        if (a.team === 'home' && b.team === 'away') return 1;
      }
      return 0;
    });
  }
  
  // Original handling for old structure
  // Special handling for h2h market (moneyline/team betting) or NCAAF markets (all come through h2h endpoint)
  if ((selectedMarket === 'h2h' || (marketData.sport === 'ncaaf' && ['spreads', 'totals'].includes(selectedMarket))) && marketData.players) {
    
    // Find a player with markets (h2h, spreads, or totals)
    const marketPlayer = marketData.players.find(p => p.markets && p.markets.length > 0);
    
    if (marketPlayer && marketPlayer.markets) {
      // Check if there's an h2h market
      const hasH2hMarket = marketPlayer.markets.some(m => m.market === 'h2h' || m.market === 'h2h_standard');
      const h2hMarket = marketPlayer.markets.find(m => m.market === 'h2h' || m.market === 'h2h_standard');
      
      if (hasH2hMarket && h2hMarket && h2hMarket.books) {
        console.log('H2H Player markets:', marketPlayer.markets);
        console.log('Full marketPlayer data:', JSON.stringify(marketPlayer, null, 2));
        
        // For NCAAF, we need to extract team names from the books data
        let teamNames = [];
        
        if (isNCAAF && h2hMarket.books) {
          // Get team names from the first bookmaker's data
          const firstBook = Object.values(h2hMarket.books)[0];
          if (firstBook) {
            teamNames = Object.keys(firstBook);
            console.log('NCAAF teams found:', teamNames);
          }
        }
        
        // Create entries based on sport type
        const teamsToProcess = isNCAAF && teamNames.length > 0 ? teamNames : ['away', 'home'];
        
        teamsToProcess.forEach((teamKey, index) => {
          const teamEntry = {
            name: isNCAAF ? teamKey : teamKey, // Use actual team name for NCAAF
            team: isNCAAF ? (index === 0 ? 'away' : 'home') : teamKey, // Map to away/home for display
            playerId: `team_${index === 0 ? 'away' : 'home'}`,
            teamId: index === 0 ? 'away' : 'home',
            point: null, // No point value for h2h
            tierKey: `team_${index === 0 ? 'away' : 'home'}_h2h`,
            starter: false,
            positionCode: null,
            position: null,
            sortOrder: index // 0 for first team (away), 1 for second team (home)
          };
          
          // Add odds for each bookmaker
          const bookPrices = [];
          Object.entries(h2hMarket.books).forEach(([bookName, bookData]) => {
            const oddsValue = isNCAAF ? bookData[teamKey] : bookData[teamKey];
            if (bookData && oddsValue !== undefined && oddsValue !== null) {
              // Apply bookmaker aliases
              const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
              teamEntry[`h2h_${displayBook}`] = oddsValue;
              bookPrices.push({ book: displayBook, price: oddsValue });
            }
          });
          
          // Calculate best book price and diff
          if (bookPrices.length > 0) {
            const bestOdds = bookPrices.reduce((best, current) => 
              current.price > best.price ? current : best
            );
            teamEntry['h2h_bestBookPrice'] = bestOdds.price;
            
            // Calculate best book diff
            const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
            if (otherPrices.length > 0) {
              const secondBest = Math.max(...otherPrices);
              if (bestOdds.price > 0 && secondBest > 0) {
                teamEntry['h2h_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
              } else if (bestOdds.price < 0 && secondBest < 0) {
                teamEntry['h2h_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
              }
            }
          }
          
          playerEntries.push(teamEntry);
        });

      }

        // Now check for and add totals (O/U) data if available
        const totalsMarkets = marketPlayer.markets.filter(m => m.market.startsWith('totals_'));
        console.log('Found totals markets:', totalsMarkets.length);
        totalsMarkets.forEach(totalsMarket => {
          if (totalsMarket && totalsMarket.books) {
            // Extract point value from market name (e.g., "totals_7.5" -> 7.5)
            const point = totalsMarket.point || parseFloat(totalsMarket.market.split('_')[1]);
            
            // Create entries for over and under
            ['over', 'under'].forEach((betType, betIndex) => {
              const tierKey = `totals_${point}_${betType}`;
              const playerEntry = {
                name: betType.toUpperCase(),
                team: null,
                playerId: `totals_${point}_${betType}`,
                teamId: null,
                point: point,
                betType: betType,
                betOrder: betIndex,
                tierKey: tierKey,
                starter: false,
                positionCode: null,
            position: null,
                sortOrder: 100 + (point * 2) + betIndex // Sort after team entries
              };

              // Add odds for each bookmaker
              const bookPrices = [];
              Object.entries(totalsMarket.books).forEach(([bookName, bookOdds]) => {
                if (bookOdds && bookOdds[betType] !== undefined && bookOdds[betType] !== null) {
                  const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                  playerEntry[`totals_${displayBook}`] = bookOdds[betType];
                  bookPrices.push({ book: displayBook, price: bookOdds[betType] });
                }
              });

              // Calculate best book price and diff for totals
              if (bookPrices.length > 0) {
                const bestOdds = bookPrices.reduce((best, current) => 
                  current.price > best.price ? current : best
                );
                playerEntry['totals_bestBookPrice'] = bestOdds.price;
                
                const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                if (otherPrices.length > 0) {
                  const secondBest = Math.max(...otherPrices);
                  if (bestOdds.price > 0 && secondBest > 0) {
                    playerEntry['totals_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
                  } else if (bestOdds.price < 0 && secondBest < 0) {
                    playerEntry['totals_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
                  }
                }
              }

              playerEntries.push(playerEntry);
            });
          }
        });

        // Now check for and add spreads data if available
        const spreadsMarkets = marketPlayer.markets.filter(m => m.market.startsWith('spreads_'));
        console.log('Found spreads markets:', spreadsMarkets.length);
        spreadsMarkets.forEach(spreadsMarket => {
          console.log('Processing spread market:', spreadsMarket.market, 'point:', spreadsMarket.point);
          if (spreadsMarket && spreadsMarket.books) {
            // Extract point value from market name (e.g., "spreads_-1.5" -> -1.5)
            const point = spreadsMarket.point || parseFloat(spreadsMarket.market.split('_')[1]);
            
            // For NCAAF, spreads use team names as keys, not away/home properties
            if (isNCAAF) {
              // Get all team names from books
              const bookNames = Object.keys(spreadsMarket.books);
              bookNames.forEach(bookName => {
                const teamData = spreadsMarket.books[bookName];
                const teamNames = Object.keys(teamData);
                
                teamNames.forEach((teamName, teamIndex) => {
                  const odds = teamData[teamName];
                  const teamKey = teamIndex === 0 ? 'away' : 'home'; // First team = away, second = home
                  const displayPoint = point > 0 ? `+${point}` : `${point}`;
                  const tierKey = `spreads_${point}_${teamKey}`;
                  
                  // Find existing entry or create new one
                  let playerEntry = playerEntries.find(p => p.playerId === `spreads_${point}_${teamKey}`);
                  if (!playerEntry) {
                    playerEntry = {
                      name: displayPoint,
                      team: teamKey,
                      playerId: `spreads_${point}_${teamKey}`,
                      teamId: teamKey,
                      point: point,
                      betType: teamKey,
                      betOrder: teamIndex,
                      tierKey: tierKey,
                      starter: false,
                      positionCode: null,
                      position: null,
                      sortOrder: 200 + (Math.abs(point) * 2) + teamIndex
                    };
                    playerEntries.push(playerEntry);
                  }
                  
                  // Add odds for this bookmaker
                  const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                  playerEntry[`spreads_${displayBook}`] = odds;
                });
              });
            } else {
              // Original logic for other sports (NFL, etc.)
              const hasAway = Object.values(spreadsMarket.books).some(odds => odds && odds.away !== undefined);
              const hasHome = Object.values(spreadsMarket.books).some(odds => odds && odds.home !== undefined);
            
            if (hasAway) {
              // Create entry for away team
              const displayPoint = point > 0 ? `+${point}` : `${point}`;
              const tierKey = `spreads_${point}_away`;
              const playerEntry = {
                name: displayPoint,
                team: 'away',
                playerId: `spreads_${point}_away`,
                teamId: 'away',
                point: point,
                betType: 'away',
                betOrder: 0,
                tierKey: tierKey,
                starter: false,
                positionCode: null,
            position: null,
                sortOrder: 200 + (Math.abs(point) * 2) // Sort after totals entries
              };

              // Add odds for each bookmaker
              const bookPrices = [];
              Object.entries(spreadsMarket.books).forEach(([bookName, bookOdds]) => {
                if (bookOdds && bookOdds.away !== undefined && bookOdds.away !== null) {
                  const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                  playerEntry[`spreads_${displayBook}`] = bookOdds.away;
                  bookPrices.push({ book: displayBook, price: bookOdds.away });
                }
              });

              // Calculate best book price and diff for spreads
              if (bookPrices.length > 0) {
                const bestOdds = bookPrices.reduce((best, current) => 
                  current.price > best.price ? current : best
                );
                playerEntry['spreads_bestBookPrice'] = bestOdds.price;
                
                const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                if (otherPrices.length > 0) {
                  const secondBest = Math.max(...otherPrices);
                  if (bestOdds.price > 0 && secondBest > 0) {
                    playerEntry['spreads_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
                  } else if (bestOdds.price < 0 && secondBest < 0) {
                    playerEntry['spreads_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
                  }
                }
              }

              console.log('Adding away spreads entry:', playerEntry);
              playerEntries.push(playerEntry);
            }
            
            if (hasHome) {
              // Create entry for home team
              const displayPoint = point > 0 ? `+${point}` : `${point}`;
              const tierKey = `spreads_${point}_home`;
              const playerEntry = {
                name: displayPoint,
                team: 'home',
                playerId: `spreads_${point}_home`,
                teamId: 'home',
                point: point,
                betType: 'home',
                betOrder: 1,
                tierKey: tierKey,
                starter: false,
                positionCode: null,
            position: null,
                sortOrder: 200 + (Math.abs(point) * 2) + 1 // Sort after totals entries
              };

              // Add odds for each bookmaker
              const bookPrices = [];
              Object.entries(spreadsMarket.books).forEach(([bookName, bookOdds]) => {
                if (bookOdds && bookOdds.home !== undefined && bookOdds.home !== null) {
                  const displayBook = BOOKMAKER_ALIASES[bookName] || bookName;
                  playerEntry[`spreads_${displayBook}`] = bookOdds.home;
                  bookPrices.push({ book: displayBook, price: bookOdds.home });
                }
              });

              // Calculate best book price and diff for spreads
              if (bookPrices.length > 0) {
                const bestOdds = bookPrices.reduce((best, current) => 
                  current.price > best.price ? current : best
                );
                playerEntry['spreads_bestBookPrice'] = bestOdds.price;
                
                const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                if (otherPrices.length > 0) {
                  const secondBest = Math.max(...otherPrices);
                  if (bestOdds.price > 0 && secondBest > 0) {
                    playerEntry['spreads_bestBookDiff'] = (bestOdds.price - secondBest) / secondBest;
                  } else if (bestOdds.price < 0 && secondBest < 0) {
                    playerEntry['spreads_bestBookDiff'] = Math.abs((1/bestOdds.price) - (1/secondBest));
                  }
                }
              }

              console.log('Adding home spreads entry:', playerEntry);
              playerEntries.push(playerEntry);
            }
            } // Close the else block for non-NCAAF sports
          }
        });
    }
  }
  
  // Process market data for player markets
  if (marketData.players) {
    marketData.players.forEach(player => {
      // Find all markets that match the selected market type (including alternates)
      // Check both the base market and the alternate market
      const matchingMarkets = player.markets.filter(m => {
        if (!m.market) return false;

        // Exact match
        if (m.market === selectedMarket) return true;

        // Alternate market with suffix (e.g., player_points_alternate_44.5)
        if (m.market.startsWith(`${selectedMarket}_alternate_`)) return true;

        // Base alternate market (e.g., player_points_alternate)
        if (m.market === `${selectedMarket}_alternate`) return true;

        // Binary market (e.g., player_double_double_binary)
        if (m.market === `${selectedMarket}_binary`) return true;

        // Check if it starts with the selected market followed by underscore and a number
        // This handles markets like "player_points_25.5" but excludes "player_points_rebounds"
        const afterMarket = m.market.substring(selectedMarket.length);
        if (afterMarket.match(/^_\d+(\.\d+)?$/)) return true;

        // IMPORTANT: Do NOT match combined stat markets
        // For example, when selectedMarket is "player_points", do NOT match:
        // - player_points_rebounds
        // - player_points_assists
        // - player_points_rebounds_assists
        // The above checks handle all valid suffixes (_alternate, _binary, _number)
        // So anything else should be rejected

        return false;
      });

      // Debug logging for Caris LeVert
      if (player.playerName === 'Caris LeVert' && selectedMarket === 'player_threes') {
        console.log('=== CARIS LEVERT MATCHING MARKETS ===', {
          playerName: player.playerName,
          selectedMarket,
          allMarkets: player.markets.map(m => m.market),
          matchingMarkets: matchingMarkets.map(m => ({ market: m.market, point: m.point })),
          matchingMarketsCount: matchingMarkets.length
        });
      }

      if (matchingMarkets.length === 0) {
        // No matching markets, create entry with base player info
        const tierKey = `${player.playerId}_${selectedMarket}_base`;
        if (!seenKeys.has(tierKey)) {
          seenKeys.add(tierKey);
          playerEntries.push({
            name: player.playerName,
            team: player.team,
            playerId: player.playerId,
            teamId: player.teamId,
            point: null,
            tierKey: tierKey,
            starter: player.starter || false,
            positionCode: player.positionCode,
            position: player.position // Preserve full position object for NFL
          });
        }
      } else {
        // Create separate entries for each tier/point value
        // Group by point value to avoid duplicates
        const pointMap = new Map();
        
        matchingMarkets.forEach(market => {
          // Check if this is an alternate market
          const isAlternate = market.market && market.market.includes('_alternate');
          
          // For markets without a point value (like base home_runs), use a default
          const point = market.point !== undefined && market.point !== null ? market.point : 0.5;
          const mapKey = String(point); // Convert to string for consistent Map keys
          
          // If we haven't seen this point value yet, or if this market has more books with data
          if (!pointMap.has(mapKey)) {
            pointMap.set(mapKey, {
              ...market,
              isAlternate: isAlternate,
              books: { ...(market.books || {}) }
            });
          } else {
            // Merge books from multiple market types (base + alternate)
            const existingMarket = pointMap.get(mapKey);
            if (market.books) {
              // Merge books, preferring non-null values
              Object.entries(market.books).forEach(([book, odds]) => {
                if (odds && ((odds.over !== null && odds.over !== undefined) || 
                           (odds.under !== null && odds.under !== undefined) ||
                           (odds.yes !== null && odds.yes !== undefined))) {
                  if (!existingMarket.books) {
                    existingMarket.books = {};
                  }
                  // Merge the odds, but prefer entries that have BOTH over and under
                  if (!existingMarket.books[book]) {
                    existingMarket.books[book] = odds;
                  } else {
                    // If existing has both over and under, don't overwrite with partial data
                    const existingHasBoth = existingMarket.books[book].over !== undefined && 
                                           existingMarket.books[book].under !== undefined;
                    const newHasBoth = odds.over !== undefined && odds.under !== undefined;
                    
                    if (existingHasBoth && !newHasBoth) {
                      // Keep existing complete data, don't overwrite with partial
                      // Debug for Cedric Tillman
                      if (player.playerName === 'Cedric Tillman' && book === 'draftkings') {
                        console.log('Skipping partial overwrite for Cedric/DK:', {
                          existing: existingMarket.books[book],
                          new: odds,
                          market: market.market
                        });
                      }
                    } else {
                      // Merge individual properties
                      existingMarket.books[book] = {
                        ...existingMarket.books[book],
                        ...odds
                      };
                    }
                  }
                }
              });
              
              // Update best book calculations after merging
              const allPrices = Object.entries(existingMarket.books)
                .filter(([_, odds]) => odds && ((odds.over !== null && odds.over !== undefined) || (odds.yes !== null && odds.yes !== undefined)))
                .map(([book, odds]) => ({ 
                  book, 
                  price: odds.yes !== null && odds.yes !== undefined ? odds.yes : odds.over 
                }));
              
              if (allPrices.length > 0) {
                const bestOdds = allPrices.reduce((best, current) => 
                  current.price > best.price ? current : best
                );
                existingMarket.bestBook = bestOdds.book;
                existingMarket.bestBookPrice = bestOdds.price;
                
                // Recalculate best book diff
                const otherPrices = allPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                if (otherPrices.length > 0) {
                  const secondBest = Math.max(...otherPrices);
                  if (bestOdds.price > 0 && secondBest > 0) {
                    existingMarket.bestBookDiff = (bestOdds.price - secondBest) / secondBest;
                  } else if (bestOdds.price < 0 && secondBest < 0) {
                    existingMarket.bestBookDiff = Math.abs((1/bestOdds.price) - (1/secondBest));
                  }
                }
              }
            }
          }
        });
        
        // Create entries from unique point values
        pointMap.forEach((market, pointStr) => {
          const point = parseFloat(pointStr);
          
          // Check if this is a mainline market that needs special handling
          const isTotalsMarket = selectedMarket === 'totals';
          const isSpreadsMarket = selectedMarket === 'spreads';
          const isYardsMarket = ['player_pass_yds', 'player_rush_yds', 'player_rush_attempts', 'player_reception_yds', 'player_receptions', 'player_total_saves', 'player_rush_reception_yds', 'player_field_goals'].includes(selectedMarket);
          const isBasketballStatMarket = ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'].includes(selectedMarket);
          
          // Debug REC market for specific player
          if (selectedMarket === 'player_receptions' && player.playerName === 'Cedric Tillman') {
            console.log('Processing Cedric Tillman REC market:', {
              market: market.market,
              point: market.point,
              isAlternate: market.isAlternate,
              hasUnder: market.books && Object.values(market.books).some(b => b && b.under !== undefined),
              books: market.books
            });
          }
          
          // For totals, check if we have under values
          const hasOverUnder = isTotalsMarket && market.books && Object.values(market.books).some(odds =>
            odds && (odds.under !== undefined || odds.over !== undefined)
          );

          // For yards markets and basketball stat markets, check if we have over/under but DON'T create separate rows
          const isYardsWithOverUnder = (isYardsMarket || isBasketballStatMarket) && market.books && Object.values(market.books).some(odds =>
            odds && (odds.under !== undefined || odds.over !== undefined)
          );

          // Debug basketball markets
          if (isBasketballStatMarket && player.playerName) {
            console.log('Basketball stat market detected:', {
              player: player.playerName,
              market: selectedMarket,
              point,
              isYardsWithOverUnder,
              hasBooks: !!market.books,
              sampleBook: market.books ? Object.entries(market.books)[0] : null
            });
          }
          
          // For spreads, check if we have home/away values  
          const hasHomeAway = isSpreadsMarket && market.books && Object.values(market.books).some(odds =>
            odds && (odds.home !== undefined || odds.away !== undefined)
          );
          
          if (hasHomeAway) {
            // Handle spreads with home/away format
            // Determine which team has which spread
            const hasAwayOdds = Object.values(market.books).some(odds => odds && odds.away !== undefined);
            const hasHomeOdds = Object.values(market.books).some(odds => odds && odds.home !== undefined);
            
            if (hasAwayOdds) {
              // Create entry for away team
              const tierKey = `${player.playerId}_${selectedMarket}_${point}_away`;
              if (!seenKeys.has(tierKey)) {
                seenKeys.add(tierKey);
                const displayPoint = point > 0 ? `+${point}` : `${point}`;
                const playerEntry = {
                  name: displayPoint,
                  team: 'away',
                  playerId: player.playerId,
                  teamId: player.teamId,
                  point: point,
                  betType: 'away',
                  betOrder: 0, // away first
                  tierKey: tierKey,
                  starter: player.starter || false,
                  positionCode: player.positionCode,
            position: player.position // Preserve full position object for NFL
                };
                
                // Extract odds for away team
                const bookPrices = [];
                Object.entries(market.books).forEach(([book, odds]) => {
                  if (odds && odds.away !== null && odds.away !== undefined) {
                    const displayBook = BOOKMAKER_ALIASES[book] || book;
                    playerEntry[`${selectedMarket}_${displayBook}`] = odds.away;
                    bookPrices.push({ book: displayBook, price: odds.away });
                  }
                });
                
                // Calculate best book for away
                if (bookPrices.length > 0) {
                  const bestOdds = bookPrices.reduce((best, current) => 
                    current.price > best.price ? current : best
                  );
                  playerEntry[`${selectedMarket}_bestBookPrice`] = bestOdds.price;
                  
                  const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                  if (otherPrices.length > 0) {
                    const secondBest = Math.max(...otherPrices);
                    if (bestOdds.price > 0 && secondBest > 0) {
                      playerEntry[`${selectedMarket}_bestBookDiff`] = (bestOdds.price - secondBest) / secondBest;
                    } else if (bestOdds.price < 0 && secondBest < 0) {
                      playerEntry[`${selectedMarket}_bestBookDiff`] = Math.abs((1/bestOdds.price) - (1/secondBest));
                    }
                  }
                }
                
                playerEntries.push(playerEntry);
              }
            }
            
            if (hasHomeOdds) {
              // Create entry for home team
              const tierKey = `${player.playerId}_${selectedMarket}_${point}_home`;
              if (!seenKeys.has(tierKey)) {
                seenKeys.add(tierKey);
                const displayPoint = point > 0 ? `+${point}` : `${point}`;
                const playerEntry = {
                  name: displayPoint,
                  team: 'home',
                  playerId: player.playerId,
                  teamId: player.teamId,
                  point: point,
                  betType: 'home',
                  betOrder: 1, // home second
                  tierKey: tierKey,
                  starter: player.starter || false,
                  positionCode: player.positionCode,
            position: player.position // Preserve full position object for NFL
                };
                
                // Extract odds for home team
                const bookPrices = [];
                Object.entries(market.books).forEach(([book, odds]) => {
                  if (odds && odds.home !== null && odds.home !== undefined) {
                    const displayBook = BOOKMAKER_ALIASES[book] || book;
                    playerEntry[`${selectedMarket}_${displayBook}`] = odds.home;
                    bookPrices.push({ book: displayBook, price: odds.home });
                  }
                });
                
                // Calculate best book for home
                if (bookPrices.length > 0) {
                  const bestOdds = bookPrices.reduce((best, current) => 
                    current.price > best.price ? current : best
                  );
                  playerEntry[`${selectedMarket}_bestBookPrice`] = bestOdds.price;
                  
                  const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                  if (otherPrices.length > 0) {
                    const secondBest = Math.max(...otherPrices);
                    if (bestOdds.price > 0 && secondBest > 0) {
                      playerEntry[`${selectedMarket}_bestBookDiff`] = (bestOdds.price - secondBest) / secondBest;
                    } else if (bestOdds.price < 0 && secondBest < 0) {
                      playerEntry[`${selectedMarket}_bestBookDiff`] = Math.abs((1/bestOdds.price) - (1/secondBest));
                    }
                  }
                }
                
                playerEntries.push(playerEntry);
              }
            }
          } else if (hasOverUnder) {
            // Create separate entries for over and under (totals only)
            // Create them in the order that will display over, then under for each point
            ['over', 'under'].forEach((betType, betIndex) => {
              const tierKey = `${player.playerId}_${selectedMarket}_${point}_${betType}`;
              if (!seenKeys.has(tierKey)) {
                seenKeys.add(tierKey);
                const playerEntry = {
                  name: selectedMarket === 'totals' ? betType.toUpperCase() : `${selectedMarket === 'spreads' ? (betType === 'over' ? '+' : '-') : betType.toUpperCase()} ${point}`,
                  team: player.team,
                  playerId: player.playerId,
                  teamId: player.teamId,
                  point: point,
                  betType: betType,
                  betOrder: betIndex, // 0 for over, 1 for under
                  tierKey: tierKey,
                  starter: player.starter || false,
                  positionCode: player.positionCode,
                  position: player.position, // Preserve full position object for NFL
                  isAlternate: market.isAlternate || false
                };
                
                // Extract odds for this bet type
                const bookPrices = [];
                Object.entries(market.books).forEach(([book, odds]) => {
                  if (odds && odds[betType] !== null && odds[betType] !== undefined) {
                    // Apply bookmaker aliases (e.g., williamhill_us -> caesars)
                    const displayBook = BOOKMAKER_ALIASES[book] || book;
                    playerEntry[`${selectedMarket}_${displayBook}`] = odds[betType];
                    bookPrices.push({ book: displayBook, price: odds[betType] });
                  }
                });
                
                // Calculate best book for this bet type
                if (bookPrices.length > 0) {
                  const bestOdds = bookPrices.reduce((best, current) => 
                    current.price > best.price ? current : best
                  );
                  playerEntry[`${selectedMarket}_bestBookPrice`] = bestOdds.price;
                  
                  // Calculate best book diff
                  const otherPrices = bookPrices.filter(p => p.book !== bestOdds.book).map(p => p.price);
                  if (otherPrices.length > 0) {
                    const secondBest = Math.max(...otherPrices);
                    if (bestOdds.price > 0 && secondBest > 0) {
                      playerEntry[`${selectedMarket}_bestBookDiff`] = (bestOdds.price - secondBest) / secondBest;
                    } else if (bestOdds.price < 0 && secondBest < 0) {
                      playerEntry[`${selectedMarket}_bestBookDiff`] = Math.abs((1/bestOdds.price) - (1/secondBest));
                    }
                  }
                }
                
                playerEntries.push(playerEntry);
              }
            });
          } else {
            // Handle player props and other markets (existing logic)
            const tierKey = `${player.playerId}_${selectedMarket}_${point}`;
            if (!seenKeys.has(tierKey)) {
              seenKeys.add(tierKey);
              // Check if this is a binary market by looking for 'yes' odds
              const isBinary = market.books && Object.values(market.books).some(odds =>
                odds && (odds.yes !== null && odds.yes !== undefined)
              );

              const playerEntry = {
                name: player.playerName,
                team: player.team,
                playerId: player.playerId,
                teamId: player.teamId,
                point: point,
                tierKey: tierKey,
                starter: player.starter || false,
                positionCode: player.positionCode,
                position: player.position, // Preserve full position object for NFL
                isAlternate: market.isAlternate || false,
                isBinary: isBinary
              };
              
              // Extract odds from the market
              if (market.books) {
                Object.entries(market.books).forEach(([book, odds]) => {
                  // Apply bookmaker aliases (e.g., williamhill_us -> caesars)
                  const displayBook = BOOKMAKER_ALIASES[book] || book;
                  
                  // For yards markets with over/under, store both values
                  if (isYardsWithOverUnder && odds) {
                    // Debug for Curry
                    if (player.playerName === 'Stephen Curry' && displayBook === 'draftkings' && point === 44.5) {
                      console.log('Curry DK 44.5 in oddsHelpers transformation:', {
                        rawOdds: odds,
                        oddsOver: odds.over,
                        oddsType: typeof odds.over,
                        market: market
                      });
                    }

                    if (odds.over !== null && odds.over !== undefined) {
                      playerEntry[`${selectedMarket}_${displayBook}_over`] = odds.over;
                    }
                    if (odds.under !== null && odds.under !== undefined) {
                      playerEntry[`${selectedMarket}_${displayBook}_under`] = odds.under;
                    }
                    // Store the point value for this bookmaker
                    playerEntry[`${selectedMarket}_${displayBook}_point`] = point;
                    // Also store combined for compatibility
                    if (odds.over !== null && odds.over !== undefined) {
                      playerEntry[`${selectedMarket}_${displayBook}`] = odds.over;
                    }

                    // Debug for Cedric Tillman
                    if (player.playerName === 'Cedric Tillman' && displayBook === 'draftkings') {
                      console.log('Cedric Tillman DraftKings entry:', {
                        over: playerEntry[`${selectedMarket}_${displayBook}_over`],
                        under: playerEntry[`${selectedMarket}_${displayBook}_under`],
                        point: playerEntry[`${selectedMarket}_${displayBook}_point`],
                        selectedMarket,
                        displayBook,
                        rawOdds: odds
                      });
                    }
                  } else {
                    // Regular markets - check for binary (yes/no) or over
                    let oddsValue = null;
                    if (odds.yes !== null && odds.yes !== undefined) {
                      oddsValue = odds.yes; // Binary market
                    } else if (odds.over !== null && odds.over !== undefined) {
                      oddsValue = odds.over; // Regular market
                    }
                    
                    if (oddsValue !== null) {
                      playerEntry[`${selectedMarket}_${displayBook}`] = oddsValue;
                    }
                  }
                });
                playerEntry[`${selectedMarket}_bestBookPrice`] = market.bestBookPrice;
                playerEntry[`${selectedMarket}_bestBookDiff`] = market.bestBookDiff;
              }
              
              // Debug final entry for Cedric Tillman
              if (player.playerName === 'Cedric Tillman' && selectedMarket === 'player_receptions') {
                console.log('Final Cedric Tillman entry:', {
                  name: playerEntry.name,
                  point: playerEntry.point,
                  draftkings_over: playerEntry.player_receptions_draftkings_over,
                  draftkings_under: playerEntry.player_receptions_draftkings_under,
                  draftkings_point: playerEntry.player_receptions_draftkings_point,
                  fanduel_over: playerEntry.player_receptions_fanduel_over,
                  fanduel_under: playerEntry.player_receptions_fanduel_under,
                  fullEntry: playerEntry
                });
              }

              // Debug Caris LeVert entries being pushed
              if (player.playerName === 'Caris LeVert' && selectedMarket === 'player_threes') {
                console.log('Caris LeVert: About to push entry:', {
                  point: playerEntry.point,
                  isAlternate: playerEntry.isAlternate,
                  tierKey: playerEntry.tierKey
                });
              }

              playerEntries.push(playerEntry);
            }
          }
        });
      }
    });
  }

  // Debug: Count how many entries were created for Caris LeVert
  if (selectedMarket === 'player_threes') {
    const carisLeVertEntries = playerEntries.filter(e => e.name === 'Caris LeVert');
    if (carisLeVertEntries.length > 0) {
      console.log('=== CARIS LEVERT FINAL ENTRIES IN oddsHelpers ===', {
        count: carisLeVertEntries.length,
        points: carisLeVertEntries.map(e => ({ point: e.point, isAlternate: e.isAlternate }))
      });
    }
  }

  return playerEntries;
};

export const calculateBestBets = (games, playersByGameId, bookmakersList, selectedMarkets, availableMarkets, masterTierFilter, getShowTiersForGame, devModeEnabled = false, recalcEdgeBestBets = false) => {
  const bestBets = [];

  games.forEach(game => {
    const gameData = playersByGameId[game.gameIDmlb];
    if (!gameData?.players) return;

    // Skip Live/In Progress/Halftime/End Period/Final games unless dev mode is enabled
    const status = game.status?.toLowerCase() || '';
    if (!devModeEnabled && (
      game.status === 'Live' ||
      status.includes('progress') ||
      status.includes('halftime') ||
      status.includes('end_period') ||
      status.includes('final') ||
      isGameFinished(game.status)
    )) return;

    // Check if the game data has h2h/spreads/totals structure (mainline markets)
    // This applies to NCAAB, NBA, WNBA, NFL, NHL, NCAAF when viewing h2h market
    const marketContainer = gameData.players[0] || {};
    const hasMainlineStructure = marketContainer.h2h || marketContainer.spreads || marketContainer.totals || marketContainer.h2h_halves || marketContainer.h2h_quarters;

    if (hasMainlineStructure) {
      const h2hArray = marketContainer.h2h || [];
      const spreadsArray = marketContainer.spreads || [];
      const totalsArray = marketContainer.totals || [];
      const h2hHalvesArray = marketContainer.h2h_halves || [];
      const h2hQuartersArray = marketContainer.h2h_quarters || [];

      // Helper to find main line for a book and side
      // Uses both _isMain and _isAlternate patterns for compatibility
      const findMainLineForBook = (array, marketPrefix, book, side = null) => {
        // First try to find by _isMain flag
        let record = array.find(m => {
          // Check for _isMain flag (newer format)
          const hasIsMain = Object.keys(m).some(key =>
            key.startsWith(`${marketPrefix}_`) &&
            key.includes(`_${book}`) &&
            key.endsWith('_isMain') &&
            m[key] === true
          );
          if (hasIsMain) {
            if (side) {
              // Check both key patterns
              return m[`${marketPrefix}_${book}_${side}`] !== undefined ||
                     m[`${marketPrefix}_${side}_${book}`] !== undefined;
            }
            return true;
          }
          return false;
        });

        if (record) return record;

        // Fallback to _isAlternate === false
        return array.find(m => {
          const hasIsAlternateFalse = m[`${marketPrefix}_${book}_isAlternate`] === false;
          if (!hasIsAlternateFalse) return false;
          if (side) {
            return m[`${marketPrefix}_${book}_${side}`] !== undefined ||
                   m[`${marketPrefix}_${side}_${book}`] !== undefined;
          }
          return true;
        }) || null;
      };

      // Helper to get odds with both key patterns
      const getOddsValue = (record, marketPrefix, book, side) => {
        // Try pattern 1: prefix_book_side
        let value = record?.[`${marketPrefix}_${book}_${side}`];
        if (value !== undefined) return value;
        // Try pattern 2: prefix_side_book
        value = record?.[`${marketPrefix}_${side}_${book}`];
        return value;
      };

      // Helper to recalculate edge for game lines using only enabled books
      const recalcGameLineEdge = (record, currentOdds, keyOrPrefix, book, side, isSpreads) => {
        const allOdds = [];
        bookmakersList.forEach(b => {
          let o;
          if (isSpreads) {
            // For spreads, key already includes sign+side, just append book
            o = record[`${keyOrPrefix}_${b}`];
          } else {
            o = getOddsValue(record, keyOrPrefix, b, side);
          }
          if (o !== null && o !== undefined && typeof o === 'number') {
            allOdds.push(o);
          }
        });
        if (allOdds.length < 2) return 0;
        const best = Math.max(...allOdds);
        const bestCount = allOdds.filter(o => o === best).length;
        if (currentOdds !== best || bestCount !== 1) return 0;
        const secondBest = Math.max(...allOdds.filter(o => o !== best));
        if (secondBest === -Infinity) return 0;
        if (best > 0 && secondBest < 0) {
          const spreadPositive = best - 100;
          const spreadNegative = secondBest - (-100);
          const totalSpread = spreadPositive - spreadNegative;
          return Math.abs(totalSpread / secondBest);
        }
        return (best - secondBest) / Math.abs(secondBest);
      };

      // Process each market type for best bets
      const processMarket = (array, marketPrefix, marketName, sides) => {
        // For spreads, we need to handle all 4 combinations
        if (marketPrefix === 'spreads') {
          const spreadCombinations = [
            { side: 'away', sign: 'plus' },
            { side: 'away', sign: 'minus' },
            { side: 'home', sign: 'plus' },
            { side: 'home', sign: 'minus' }
          ];

          // Process ALL records (including alts)
          array.forEach(record => {
            const point = record.point;
            const isAltRecord = record.isAlternate === true;

            spreadCombinations.forEach(combo => {
              const { side, sign } = combo;
              const key = `spreads_${sign}_${side}`;

              // Count ALL books that have odds for this line (not just enabled books)
              // This ensures bets aren't excluded just because a non-best book is disabled
              const oddsPatternForCount = new RegExp(`^${key}_([a-z_]+)$`);
              let totalMarketCount = 0;
              let totalAvailableCount = 0;
              Object.keys(record).forEach(recordKey => {
                const match = recordKey.match(oddsPatternForCount);
                if (match && !recordKey.includes('_is') && !recordKey.includes('best') && !recordKey.includes('next')) {
                  const bookOdds = record[recordKey];
                  if (bookOdds !== undefined && bookOdds !== null && typeof bookOdds === 'number') {
                    totalMarketCount++;
                    if (record[`${recordKey}_isAvailable`] !== false) {
                      totalAvailableCount++;
                    }
                  }
                }
              });

              bookmakersList.forEach(book => {
                const odds = record[`${key}_${book}`];
                const bestPrice = record[`spreads_bestBookPrice_${sign}_${side}`];
                const bestDiff = recalcEdgeBestBets
                  ? recalcGameLineEdge(record, odds, key, book, null, true)
                  : (record[`spreads_bestBookDiff_${sign}_${side}`] || 0);

                if (odds === undefined || odds === null) return;

                if (odds === bestPrice && Math.abs(bestDiff) >= 0.05 && totalMarketCount >= 3 && totalAvailableCount >= 3) {
                  // Skip if best book's odds are not available
                  if (record[`${key}_${book}_isAvailable`] === false) return;

                  // Build allOdds object - scan record for all books that have odds
                  const allOdds = {};
                  const oddsPattern = new RegExp(`^${key}_(.+)$`);
                  Object.keys(record).forEach(recordKey => {
                    const match = recordKey.match(oddsPattern);
                    if (match && !recordKey.includes('_is') && !recordKey.includes('best') && !recordKey.includes('next')) {
                      const rawBookName = match[1];
                      const bookName = BOOKMAKER_ALIASES[rawBookName] || rawBookName;
                      const bookOdds = record[recordKey];
                      if (bookOdds !== undefined && bookOdds !== null && typeof bookOdds === 'number') {
                        allOdds[bookName] = bookOdds;
                        // Also copy availability flag if present
                        const isAvailable = record[`${recordKey}_isAvailable`];
                        if (isAvailable !== undefined) {
                          allOdds[`${bookName}_isAvailable`] = isAvailable;
                        }
                      }
                    }
                  });

                  const teamName = side === 'home' ? game.home : game.away;
                  const pointNum = point !== undefined ? parseFloat(point) : null;
                  const pointDisplay = pointNum !== null ? (sign === 'plus' ? `+${pointNum}` : `-${pointNum}`) : '';
                  const displayName = `${teamName} ${pointDisplay}`.trim();

                  bestBets.push({
                    player: displayName,
                    team: teamName,
                    teamLogo: side === 'home' ? game.homeLogo : game.awayLogo,
                    market: isAltRecord ? 'Alt Spread' : marketName,
                    marketKey: marketPrefix,
                    book: book,
                    odds: `${odds > 0 ? '+' : ''}${odds}`,
                    advantage: Math.abs(bestDiff),
                    tier: Math.abs(bestDiff) >= 0.25 ? 'edge-one' : Math.abs(bestDiff) >= 0.15 ? 'edge-two' : 'edge-three',
                    competingOdds: '',
                    game: `${game.away} @ ${game.home}`,
                    gameId: game.gameIDmlb,
                    playerId: null,
                    awayTeam: game.away,
                    homeTeam: game.home,
                    teamId: side === 'home' ? (game.homeTeamId || game.homeId) : (game.awayTeamId || game.awayId),
                    playerTeam: teamName,
                    gameTime: game.startTimeEST || game.gameTime,
                    gameStatus: game.status,
                    allOdds: allOdds,
                    point: sign === 'minus' ? -pointNum : pointNum,
                    side: side,
                    starter: false,
                    battingOrder: null,
                    awayPitcher: null,
                    homePitcher: null,
                    betType: sign === 'plus' ? 'Underdog' : 'Favorite',
                    position: null,
                    homeConference: game.homeConferenceName,
                    awayConference: game.awayConferenceName,
                    homeRank: game.homeRank,
                    awayRank: game.awayRank
                  });
                }
              });
            });
          });
        } else if (marketPrefix === 'totals') {
          // Process ALL totals records (including alts)
          array.forEach(record => {
            const point = record.point;
            const isAltRecord = record.isAlternate === true;

            sides.forEach(side => {
              // Count ALL books that have odds for this side (not just enabled books)
              // This ensures bets aren't excluded just because a non-best book is disabled
              const oddsPatternForCount = new RegExp(`^${marketPrefix}_${side}_([a-z_]+)$`);
              let totalMarketCount = 0;
              let totalAvailableCount = 0;
              Object.keys(record).forEach(recordKey => {
                const match = recordKey.match(oddsPatternForCount);
                if (match && !recordKey.includes('_is') && !recordKey.includes('best') && !recordKey.includes('next')) {
                  const bookOdds = record[recordKey];
                  if (bookOdds !== undefined && bookOdds !== null && typeof bookOdds === 'number') {
                    totalMarketCount++;
                    if (record[`${recordKey}_isAvailable`] !== false) {
                      totalAvailableCount++;
                    }
                  }
                }
              });

              bookmakersList.forEach(book => {
                const odds = getOddsValue(record, marketPrefix, book, side);
                const bestPrice = record[`${marketPrefix}_bestBookPrice_${side}`];
                const bestDiff = recalcEdgeBestBets
                  ? recalcGameLineEdge(record, odds, marketPrefix, book, side, false)
                  : (record[`${marketPrefix}_bestBookDiff_${side}`] || 0);

                if (odds === undefined || odds === null) return;

                if (odds === bestPrice && Math.abs(bestDiff) >= 0.05 && totalMarketCount >= 3 && totalAvailableCount >= 3) {
                  // Skip if best book's odds are not available
                  if (record[`${marketPrefix}_${side}_${book}_isAvailable`] === false) return;

                  // Build allOdds object - scan record for all books that have odds
                  const allOdds = {};
                  const oddsPattern = new RegExp(`^${marketPrefix}_${side}_(.+)$`);
                  Object.keys(record).forEach(key => {
                    const match = key.match(oddsPattern);
                    if (match && !key.includes('_is') && !key.includes('best') && !key.includes('next')) {
                      const rawBookName = match[1];
                      const bookName = BOOKMAKER_ALIASES[rawBookName] || rawBookName;
                      const odds = record[key];
                      if (odds !== undefined && odds !== null && typeof odds === 'number') {
                        allOdds[bookName] = odds;
                        // Also copy availability flag if present
                        const isAvailable = record[`${key}_isAvailable`];
                        if (isAvailable !== undefined) {
                          allOdds[`${bookName}_isAvailable`] = isAvailable;
                        }
                      }
                    }
                  });

                  const betType = side === 'over' ? 'Over' : 'Under';
                  const displayName = `${betType} ${point}`;

                  bestBets.push({
                    player: displayName,
                    team: null,
                    teamLogo: null,
                    market: isAltRecord ? 'Alt Total' : marketName,
                    marketKey: marketPrefix,
                    book: book,
                    odds: `${odds > 0 ? '+' : ''}${odds}`,
                    advantage: Math.abs(bestDiff),
                    tier: Math.abs(bestDiff) >= 0.25 ? 'edge-one' : Math.abs(bestDiff) >= 0.15 ? 'edge-two' : 'edge-three',
                    competingOdds: '',
                    game: `${game.away} @ ${game.home}`,
                    gameId: game.gameIDmlb,
                    playerId: null,
                    awayTeam: game.away,
                    homeTeam: game.home,
                    playerTeam: null,
                    gameTime: game.startTimeEST || game.gameTime,
                    gameStatus: game.status,
                    allOdds: allOdds,
                    point: point,
                    side: side,
                    starter: false,
                    battingOrder: null,
                    awayPitcher: null,
                    homePitcher: null,
                    betType: betType,
                    position: null,
                    homeConference: game.homeConferenceName,
                    awayConference: game.awayConferenceName,
                    homeRank: game.homeRank,
                    awayRank: game.awayRank
                  });
                }
              });
            });
          });
        } else {
          // H2H and Totals processing
          sides.forEach(side => {
            bookmakersList.forEach(book => {
              const bookRecord = findMainLineForBook(array, marketPrefix, book, side);
              if (!bookRecord) return;

              const odds = getOddsValue(bookRecord, marketPrefix, book, side);
              const bestPrice = bookRecord[`${marketPrefix}_bestBookPrice_${side}`];
              const bestDiff = recalcEdgeBestBets
                ? recalcGameLineEdge(bookRecord, odds, marketPrefix, book, side, false)
                : (bookRecord[`${marketPrefix}_bestBookDiff_${side}`] || 0);

              if (odds === undefined || odds === null) return;

              // Count ALL books that have odds in this record (not just enabled books)
              // This ensures bets aren't excluded just because a non-best book is disabled
              // Try both patterns: prefix_book_side and prefix_side_book
              const pattern1 = new RegExp(`^${marketPrefix}_([a-z_]+)_${side}$`);
              const pattern2 = new RegExp(`^${marketPrefix}_${side}_([a-z_]+)$`);
              let totalMarketCount = 0;
              let totalAvailableCount = 0;
              Object.keys(bookRecord).forEach(recordKey => {
                let match = recordKey.match(pattern1) || recordKey.match(pattern2);
                if (match && !recordKey.includes('_is') && !recordKey.includes('best') && !recordKey.includes('next')) {
                  const bookOdds = bookRecord[recordKey];
                  if (bookOdds !== undefined && bookOdds !== null && typeof bookOdds === 'number') {
                    totalMarketCount++;
                    if (bookRecord[`${recordKey}_isAvailable`] !== false) {
                      totalAvailableCount++;
                    }
                  }
                }
              });

              if (odds === bestPrice && Math.abs(bestDiff) >= 0.05 && totalMarketCount >= 3 && totalAvailableCount >= 3) {
                // Skip if best book's odds are not available
                if (bookRecord[`${marketPrefix}_${book}_${side}_isAvailable`] === false) return;

                // Build allOdds object - scan record for all books that have odds at this point
                const allOdds = {};
                // Try both patterns: prefix_book_side and prefix_side_book
                const pattern1 = new RegExp(`^${marketPrefix}_(.+)_${side}$`);
                const pattern2 = new RegExp(`^${marketPrefix}_${side}_(.+)$`);
                Object.keys(bookRecord).forEach(recordKey => {
                  let match = recordKey.match(pattern1) || recordKey.match(pattern2);
                  if (match && !recordKey.includes('_is') && !recordKey.includes('best') && !recordKey.includes('next')) {
                    const rawBookName = match[1];
                    const bookName = BOOKMAKER_ALIASES[rawBookName] || rawBookName;
                    const bookOdds = bookRecord[recordKey];
                    if (bookOdds !== undefined && bookOdds !== null && typeof bookOdds === 'number') {
                      allOdds[bookName] = bookOdds;
                      // Also copy availability flag if present
                      const isAvailable = bookRecord[`${recordKey}_isAvailable`];
                      if (isAvailable !== undefined) {
                        allOdds[`${bookName}_isAvailable`] = isAvailable;
                      }
                    }
                  }
                });

                // Determine team name and bet type
                let teamName = side === 'home' ? game.home : side === 'away' ? game.away : null;
                let betType = side === 'over' ? 'Over' : side === 'under' ? 'Under' : null;
                let point = bookRecord.point;

                // For spreads, include the point in the display name
                let displayName;
                if (marketPrefix === 'spreads' && point !== null && point !== undefined) {
                  const pointDisplay = point > 0 ? `+${point}` : point;
                  displayName = `${teamName} ${pointDisplay}`;
                } else {
                  displayName = teamName || betType || side;
                }

                bestBets.push({
                  player: displayName,
                  team: teamName,
                  teamLogo: side === 'home' ? game.homeLogo : side === 'away' ? game.awayLogo : null,
                  market: marketName,
                  marketKey: marketPrefix,
                  book: book,
                  odds: `${odds > 0 ? '+' : ''}${odds}`,
                  advantage: Math.abs(bestDiff),
                  tier: Math.abs(bestDiff) >= 0.25 ? 'edge-one' : Math.abs(bestDiff) >= 0.15 ? 'edge-two' : 'edge-three',
                  competingOdds: '',
                  game: `${game.away} @ ${game.home}`,
                  gameId: game.gameIDmlb,
                  playerId: null,
                  awayTeam: game.away,
                  homeTeam: game.home,
                  teamId: side === 'home' ? (game.homeTeamId || game.homeId) : side === 'away' ? (game.awayTeamId || game.awayId) : null,
                  playerTeam: teamName,
                  gameTime: game.startTimeEST || game.gameTime,
                  gameStatus: game.status,
                  allOdds: allOdds,
                  point: point,
                  side: side,
                  starter: false,
                  battingOrder: null,
                  awayPitcher: null,
                  homePitcher: null,
                  betType: betType,
                  position: null,
                  homeConference: game.homeConferenceName,
                  awayConference: game.awayConferenceName,
                  homeRank: game.homeRank,
                  awayRank: game.awayRank
                });
              }
            });
          });
        }
      };

      // Process H2H (moneyline) - full game only
      processMarket(h2hArray, 'h2h', 'Moneyline', ['home', 'away']);

      // Process Spreads (all lines including alts)
      processMarket(spreadsArray, 'spreads', 'Spread', ['home', 'away']);

      // Process Totals (all lines including alts)
      processMarket(totalsArray, 'totals', 'Totals', ['over', 'under']);

      // Process Halves H2H (for NCAAF)
      if (h2hHalvesArray.length > 0) {
        processMarket(h2hHalvesArray, 'h2h', '1st Half ML', ['home', 'away']);
      }

      // Process Quarters H2H (for NCAAF)
      if (h2hQuartersArray.length > 0) {
        processMarket(h2hQuartersArray, 'h2h', '1st Qtr ML', ['home', 'away']);
      }

      // For NCAAB and NCAAF, skip player props processing (they only have mainline markets)
      if (game.sport === 'ncaab' || game.sport === 'ncaaf') {
        return;
      }
      // For other sports, continue to player props if they have player data
      // (this allows NFL, NBA, etc. to show both mainline and player prop best bets)
    }

    // Get the selected market for this game (fallback to sport-specific default)
    const sportDefault = getDefaultMarketForSport(game.sport);
    const selectedMarket = selectedMarkets[game.gameIDmlb] || sportDefault;

    console.log('Best Bets Debug:', {
      sport: game.sport,
      gameId: game.gameIDmlb,
      selectedMarket,
      sportDefault,
      playerCount: gameData.players.length
    });
    
    // Find market display name
    const marketInfo = availableMarkets.find(m => m.key === selectedMarket);
    const marketDisplayName = marketInfo?.name || selectedMarket;

    // Best Bets should search ALL lines in the endpoint, not just what's filtered on screen
    // This ensures we find the best edges across all tiers/alternates
    const allPlayers = gameData.players;

    // Game-level over/under markets with per-side best-book metadata (e.g.
    // soccer corners totals). The main player-prop path below only looks at
    // the _over side and reads `${market}_bestBookPrice` without a side
    // suffix — which doesn't exist on these records — so we handle both
    // sides here against the per-side best-book keys the backend emits.
    const isTwoSidedGameMarket = selectedMarket === 'totals_corners' || selectedMarket === 'team_totals_corners';
    if (isTwoSidedGameMarket) {
      allPlayers.forEach(record => {
        if (!record || record.point === null || record.point === undefined) return;
        const point = Number(record.point);
        ['over', 'under'].forEach(side => {
          // Count books with odds for THIS side at this tier
          const sidePattern = new RegExp(`^${selectedMarket}_([a-z0-9_]+)_${side}$`);
          let totalMarketCount = 0;
          let totalAvailableCount = 0;
          Object.keys(record).forEach(k => {
            if (!sidePattern.test(k)) return;
            if (k.includes('bestBook') || k.includes('nextBook') || k.endsWith('_isAvailable') || k.endsWith('_isAlternate')) return;
            const v = record[k];
            if (v === null || v === undefined || typeof v !== 'number') return;
            totalMarketCount++;
            if (record[`${k}_isAvailable`] !== false) totalAvailableCount++;
          });
          if (totalMarketCount < 3 || totalAvailableCount < 3) return;

          const bestPrice = record[`${selectedMarket}_bestBookPrice_${side}`];
          if (bestPrice === undefined || bestPrice === null) return;

          // The server emits 0 for bestBookDiff when the best book is tied
          // ('multi') — fall back to bestBooksDiff (edge vs. next-best book)
          // in that case so tied-best rows still surface.
          const rawDiff = record[`${selectedMarket}_bestBookDiff_${side}`];
          const diff = (rawDiff && rawDiff !== 0)
            ? rawDiff
            : (record[`${selectedMarket}_bestBooksDiff_${side}`] || 0);
          if (Math.abs(diff) < 0.05) return;

          // Collect every book's odds for this side (for the Best Bets detail
          // view / allOdds comparison column).
          const allOdds = {};
          Object.keys(record).forEach(k => {
            const m = k.match(sidePattern);
            if (!m) return;
            if (k.includes('bestBook') || k.includes('nextBook') || k.endsWith('_isAvailable') || k.endsWith('_isAlternate')) return;
            const rawBookName = m[1];
            const bookName = BOOKMAKER_ALIASES[rawBookName] || rawBookName;
            const v = record[k];
            if (v === null || v === undefined || typeof v !== 'number') return;
            allOdds[bookName] = v;
            const avail = record[`${k}_isAvailable`];
            if (avail !== undefined) allOdds[`${bookName}_isAvailable`] = avail;
          });

          // Push one Best Bet per side (whichever book carries the best price).
          // If multiple books tie, prefer the first enabled one.
          let pushed = false;
          bookmakersList.forEach(book => {
            if (pushed) return;
            const aliasBook = Object.keys(BOOKMAKER_ALIASES).find(k => BOOKMAKER_ALIASES[k] === book);
            const price = record[`${selectedMarket}_${book}_${side}`] ?? (aliasBook ? record[`${selectedMarket}_${aliasBook}_${side}`] : undefined);
            if (price === undefined || price === null) return;
            if (price !== bestPrice) return;
            if (record[`${selectedMarket}_${book}_${side}_isAvailable`] === false) return;

            const sideLabel = side === 'over' ? 'O' : 'U';
            const teamOrGameLabel = selectedMarket === 'team_totals_corners'
              ? `${record.teamAbbr || record.team || record.teamName || ''} ${sideLabel} ${point}`.trim()
              : `${sideLabel} ${point}`;

            bestBets.push({
              player: teamOrGameLabel,
              team: null,
              teamLogo: null,
              market: marketDisplayName,
              marketKey: selectedMarket,
              book,
              odds: `${price > 0 ? '+' : ''}${price}`,
              advantage: Math.abs(diff),
              tier: Math.abs(diff) >= 0.25 ? 'edge-one' : Math.abs(diff) >= 0.15 ? 'edge-two' : 'edge-three',
              competingOdds: '',
              game: `${game.away} @ ${game.home}`,
              gameId: game.gameIDmlb,
              playerId: null,
              awayTeam: game.away,
              homeTeam: game.home,
              teamId: null,
              awayLogo: game.awayLogo,
              homeLogo: game.homeLogo,
              point,
              side,
              allOdds,
              sport: game.sport,
              gameTime: game.gameTime,
              gameStatus: game.status
            });
            pushed = true;
          });
        });
      });
      return; // skip the player-prop path for this game
    }

    allPlayers.forEach((player, idx) => {
      const enabledBooks = bookmakersList;

      // Determine if this is a binary market (no side) or O/U market (needs _over suffix)
      const isBinary = isBinaryMarket(selectedMarket);
      const fieldSuffix = isBinary ? '' : '_over';

      // Count ALL books that have odds for this market (not just enabled books)
      // This determines if there's enough data to consider this a valid best bet
      // The enabled books filter only affects whether the BEST book is excluded
      const oddsPattern = new RegExp(`^${selectedMarket}_([a-z_]+)${fieldSuffix}$`);
      let totalMarketCount = 0;
      let totalAvailableCount = 0;
      Object.keys(player).forEach(key => {
        const match = key.match(oddsPattern);
        if (match && !key.includes('best') && !key.includes('next') && !key.endsWith('_isAvailable')) {
          const odds = player[key];
          if (odds !== null && odds !== undefined && typeof odds === 'number') {
            totalMarketCount++;
            // Check availability
            if (player[`${key}_isAvailable`] !== false) {
              totalAvailableCount++;
            }
          }
        }
      });

      // Also count enabled books for backward compatibility in logging
      const marketCount = enabledBooks.filter(book =>
        player[`${selectedMarket}_${book}${fieldSuffix}`] !== null && player[`${selectedMarket}_${book}${fieldSuffix}`] !== undefined
      ).length;

      // Count how many enabled books have AVAILABLE odds
      const availableCount = enabledBooks.filter(book =>
        player[`${selectedMarket}_${book}${fieldSuffix}`] !== null &&
        player[`${selectedMarket}_${book}${fieldSuffix}`] !== undefined &&
        player[`${selectedMarket}_${book}${fieldSuffix}_isAvailable`] !== false
      ).length;

      // Debug first player
      if (idx === 0) {
        console.log('First player best bets check:', {
          playerName: player.name,
          selectedMarket,
          isBinary,
          fieldSuffix,
          totalMarketCount,
          totalAvailableCount,
          enabledBookCount: marketCount,
          enabledBooks,
          sampleKey: `${selectedMarket}_${enabledBooks[0]}${fieldSuffix}`,
          sampleValue: player[`${selectedMarket}_${enabledBooks[0]}${fieldSuffix}`],
          playerKeys: Object.keys(player).filter(k => k.includes(selectedMarket))
        });
      }

      // Use totalMarketCount to check if there's enough data (3+ books with odds)
      // This ensures bets aren't excluded just because a non-best book is disabled
      if (totalMarketCount > 1) {
        // Only one eligible Best Bets row per player+market+point. If multiple
        // books are tied at the primary best, we keep the first one encountered.
        let eligiblePushedForProp = false;
        enabledBooks.forEach(book => {
          if (eligiblePushedForProp) return;
          const price = player[`${selectedMarket}_${book}${fieldSuffix}`];
          if (price === undefined || price === null) return;

          // Edge calculation: recalculate client-side or use server-provided bestBookDiff
          let bestDiff;
          if (recalcEdgeBestBets) {
            bestDiff = getEdgeForPlayerBook(player, book, selectedMarket, bookmakersList);
          } else {
            const bestPrice = player[`${selectedMarket}_bestBookPrice`];
            bestDiff = (price === bestPrice) ? (player[`${selectedMarket}_bestBookDiff`] || 0) : 0;
          }

          // Use totalMarketCount and totalAvailableCount for the >= 3 checks
          // This way, excluding a non-best book won't filter out the bet
          // getEdgeForPlayerBook returns 0 if this book doesn't have the unique best odds
          if (Math.abs(bestDiff) >= 0.05 && totalMarketCount >= 3 && totalAvailableCount >= 3) {
            // Skip if best book's odds are not available
            if (player[`${selectedMarket}_${book}${fieldSuffix}_isAvailable`] === false) return;

            const competingOdds = enabledBooks
              .filter(b => b !== book && player[`${selectedMarket}_${b}${fieldSuffix}`] !== null && player[`${selectedMarket}_${b}${fieldSuffix}`] !== undefined)
              .map(b => {
                const odds = player[`${selectedMarket}_${b}${fieldSuffix}`];
                return `${b.charAt(0).toUpperCase() + b.slice(1)}: ${odds > 0 ? '+' : ''}${odds}`;
              })
              .join(', ');

            // Build allOdds object dynamically - scan player object for all books that have odds
            const allOdds = {};
            const oddsPattern = new RegExp(`^${selectedMarket}_(.+)${fieldSuffix}$`);
            Object.keys(player).forEach(playerKey => {
              const match = playerKey.match(oddsPattern);
              if (match && !playerKey.includes('_is') && !playerKey.includes('best') && !playerKey.includes('next')) {
                const rawBookName = match[1];
                const bookName = BOOKMAKER_ALIASES[rawBookName] || rawBookName;
                const odds = player[playerKey];
                if (odds !== undefined && odds !== null && typeof odds === 'number') {
                  allOdds[bookName] = odds;
                  // Also copy availability flag if present
                  const isAvailable = player[`${playerKey}_isAvailable`];
                  if (isAvailable !== undefined) {
                    allOdds[`${bookName}_isAvailable`] = isAvailable;
                  }
                }
              }
            });

            // Determine display name based on market type
            let displayName = player.name;
            if (selectedMarket === 'h2h') {
              // For h2h, show team name instead of "away" or "home"
              if (player.team === 'away') {
                displayName = game.away;
              } else if (player.team === 'home') {
                displayName = game.home;
              }
            } else if (selectedMarket === 'spreads' && player.betType) {
              // For spreads, show team name with spread value
              if (player.team === 'away') {
                displayName = `${game.away} ${player.name}`;
              } else if (player.team === 'home') {
                displayName = `${game.home} ${player.name}`;
              } else {
                displayName = player.name;
              }
            } else if (selectedMarket === 'totals' && player.betType) {
              // For totals, show shortened bet description (O 8.5, U 8.5)
              // The game matchup is already shown in the first column
              displayName = player.name.replace('OVER', 'O').replace('UNDER', 'U');
            }

            // For NHL, extract position abbreviation separately
            let positionAbbrev = null;
            if (game.sport === 'nhl' && player.position) {
              const positionStr = String(player.position).toUpperCase();

              // Map NHL positions to F or D
              if (['FORWARD', 'C', 'LW', 'RW', 'W', 'F', 'CENTER', 'LEFTWING', 'RIGHTWING', 'WING'].includes(positionStr)) {
                positionAbbrev = 'F';
              } else if (['DEFENSEMAN', 'D', 'LD', 'RD', 'DEFENSE'].includes(positionStr)) {
                positionAbbrev = 'D';
              }
            }

            // For NBA, extract position abbreviation
            if (game.sport === 'nba' && player.position) {
              const positionStr = String(player.position).toUpperCase();
              // Map NBA positions to standard abbreviations
              if (['POINT GUARD', 'PG'].includes(positionStr)) {
                positionAbbrev = 'PG';
              } else if (['SHOOTING GUARD', 'SG'].includes(positionStr)) {
                positionAbbrev = 'SG';
              } else if (['SMALL FORWARD', 'SF'].includes(positionStr)) {
                positionAbbrev = 'SF';
              } else if (['POWER FORWARD', 'PF'].includes(positionStr)) {
                positionAbbrev = 'PF';
              } else if (['CENTER', 'C'].includes(positionStr)) {
                positionAbbrev = 'C';
              } else if (['GUARD', 'G'].includes(positionStr)) {
                positionAbbrev = 'G';
              } else if (['FORWARD', 'F'].includes(positionStr)) {
                positionAbbrev = 'F';
              } else {
                positionAbbrev = positionStr;
              }
            }

            // Determine actual team name for logo display
            let actualTeam = player.team;
            let teamLogo = null;

            if (selectedMarket === 'spreads' || selectedMarket === 'h2h') {
              if (player.team === 'away') {
                actualTeam = game.away;
                teamLogo = game.awayLogo; // Use logo from game data if available
              } else if (player.team === 'home') {
                actualTeam = game.home;
                teamLogo = game.homeLogo; // Use logo from game data if available
              }
            } else if (selectedMarket === 'totals') {
              // For totals, don't show team logo
              actualTeam = null;
            } else {
              // For player props, check if we have logo in game data
              if (player.team === game.away) {
                teamLogo = game.awayLogo;
              } else if (player.team === game.home) {
                teamLogo = game.homeLogo;
              }
            }


            // Helper function to get batting order position for a player (1-based)
            const getBattingOrderPosition = (player) => {
              if (game.sport !== 'mlb' || selectedMarket === 'h2h' || selectedMarket === 'pitcher_strikeouts') {
                return null;
              }
              
              // Extract numeric ID from player.playerId
              const playerIdStr = player.playerId?.toString() || '';
              const numericId = playerIdStr.includes('_') ? 
                parseInt(playerIdStr.split('_')[1]) : 
                parseInt(playerIdStr);
              
              if (isNaN(numericId)) return null;
              
              // Check away lineup if it exists
              if (game.awayStartingLineup && game.awayStartingLineup.length > 0) {
                const awayIndex = game.awayStartingLineup.findIndex(id => parseInt(id) === numericId);
                if (awayIndex !== -1 && awayIndex < 9) { // Exclude pitcher (index 9)
                  return awayIndex + 1; // Convert to 1-based position
                }
              }
              
              // Check home lineup if it exists
              if (game.homeStartingLineup && game.homeStartingLineup.length > 0) {
                const homeIndex = game.homeStartingLineup.findIndex(id => parseInt(id) === numericId);
                if (homeIndex !== -1 && homeIndex < 9) { // Exclude pitcher (index 9)
                  return homeIndex + 1; // Convert to 1-based position
                }
              }
              
              return null;
            };

            const battingOrder = getBattingOrderPosition(player);

            bestBets.push({
              player: displayName,
              team: actualTeam,
              teamLogo: teamLogo, // Include team logo URL from game data
              market: marketDisplayName,
              marketKey: selectedMarket,
              book: book,
              odds: `${price > 0 ? '+' : ''}${price}`,
              advantage: Math.abs(bestDiff),
              tier: Math.abs(bestDiff) >= 0.25 ? 'edge-one' : Math.abs(bestDiff) >= 0.15 ? 'edge-two' : Math.abs(bestDiff) >= 0.05 ? 'edge-three' : 'no-edge',
              competingOdds: competingOdds,
              game: `${game.away} @ ${game.home}`,
              gameId: game.gameIDmlb,
              playerId: player.playerId,
              awayTeam: game.away,
              homeTeam: game.home,
              playerTeam: actualTeam,
              gameTime: game.startTimeEST || game.gameTime,
              gameStatus: game.status,
              allOdds: allOdds,
              point: player.point, // Include tier info
              starter: player.starter || false, // Include starter info
              battingOrder: battingOrder, // Include batting order position
              awayPitcher: game.awayProbablePitcher?.fullName || 'TBD',
              homePitcher: game.homeProbablePitcher?.fullName || 'TBD',
              betType: player.betType || (player.point !== null && player.point !== undefined ? 'over' : null), // Include bet type, default to 'over' for player props with points
              position: positionAbbrev, // Include NHL position abbreviation (F or D)
              homeConference: game.homeConferenceName,
              awayConference: game.awayConferenceName,
              homeRank: game.homeRank,
              awayRank: game.awayRank
            });
            eligiblePushedForProp = true;
          }
        });

        // Secondary books (e.g. Kalshi, Circa) — they can't be the primary
        // best. If any of them ties or beats the API's bestBookPrice on this
        // prop we either tag the existing eligible-book Best Bets entry (caret
        // marker) OR — if no eligible book qualified for a Best Bet — push a
        // standalone secondary entry so the row still appears in the list.
        const bestPriceForSecondary = player[`${selectedMarket}_bestBookPrice`];
        if (bestPriceForSecondary !== null && bestPriceForSecondary !== undefined) {
          const secondaryPattern = new RegExp(`^${selectedMarket}_(.+)${fieldSuffix}$`);
          const secondaryMatches = [];
          Object.keys(player).forEach(playerKey => {
            const match = playerKey.match(secondaryPattern);
            if (!match) return;
            if (playerKey.includes('_is') || playerKey.includes('best') || playerKey.includes('next')) return;
            const rawBookName = match[1];
            const bookName = BOOKMAKER_ALIASES[rawBookName] || rawBookName;
            if (!isExcludedFromBestBook(bookName)) return;
            // Respect the user's Best-Bets book prefs — don't surface a
            // Secondary book they've disabled.
            if (!enabledBooks.includes(bookName)) return;
            const exPrice = player[playerKey];
            if (exPrice === null || exPrice === undefined || typeof exPrice !== 'number') return;
            if (player[`${playerKey}_isAvailable`] === false) return;
            const tiesOrBeats =
              (exPrice > 0 && bestPriceForSecondary > 0 && exPrice >= bestPriceForSecondary) ||
              (exPrice < 0 && bestPriceForSecondary < 0 && exPrice >= bestPriceForSecondary);
            if (!tiesOrBeats) return;
            secondaryMatches.push({ book: bookName, price: exPrice });
          });

          if (secondaryMatches.length > 0) {
            // Find any entries we just pushed for this player+market+point.
            const taggedIndices = [];
            for (let i = bestBets.length - 1; i >= 0; i--) {
              const entry = bestBets[i];
              if (
                entry.playerId === player.playerId &&
                entry.marketKey === selectedMarket &&
                entry.point === player.point
              ) {
                taggedIndices.push(i);
              } else {
                break; // entries are pushed contiguously per player+market+point
              }
            }

            if (taggedIndices.length > 0) {
              // Eligible entry exists — attach secondaryMatches as a caret hint.
              taggedIndices.forEach(i => { bestBets[i].secondaryMatches = secondaryMatches; });
            } else {
              // No eligible primary Best Bet for this prop — but a Secondary
              // book (Kalshi, Circa, …) strictly beats the primary's best
              // price. Surface a row for the SECONDARY book that actually has
              // the edge — not the primary. The row's `book` / `odds` / `tier`
              // all belong to the Secondary so the coloring reflects reality
              // (previously we were tagging the primary book with the
              // Secondary's edge, which made e.g. DraftKings look "green"
              // when its own edge was only ~2%).
              const beats = secondaryMatches.filter(s => {
                if (s.price <= bestPriceForSecondary) return false; // strict beat only
                const sameSign =
                  (s.price > 0 && bestPriceForSecondary > 0) ||
                  (s.price < 0 && bestPriceForSecondary < 0);
                return sameSign;
              });
              if (beats.length > 0) {
                // Pick the Secondary book with the widest edge over the primary
                // — that's the row's "best" price / book / tier.
                const topSecondary = beats.reduce((best, cur) => (cur.price > best.price ? cur : best), beats[0]);
                const topEdge = Math.abs((topSecondary.price - bestPriceForSecondary) / bestPriceForSecondary);
                const tier = topEdge >= 0.25 ? 'edge-one'
                  : topEdge >= 0.15 ? 'edge-two'
                  : 'edge-three';

                // Per-player metadata (same derivation as the eligible push).
                let displayName = player.name;
                if (selectedMarket === 'h2h') {
                  if (player.team === 'away') displayName = game.away;
                  else if (player.team === 'home') displayName = game.home;
                } else if (selectedMarket === 'spreads' && player.betType) {
                  if (player.team === 'away') displayName = `${game.away} ${player.name}`;
                  else if (player.team === 'home') displayName = `${game.home} ${player.name}`;
                } else if (selectedMarket === 'totals' && player.betType) {
                  displayName = player.name.replace('OVER', 'O').replace('UNDER', 'U');
                }
                let actualTeam = player.team;
                let teamLogo = null;
                if (selectedMarket === 'spreads' || selectedMarket === 'h2h') {
                  if (player.team === 'away') { actualTeam = game.away; teamLogo = game.awayLogo; }
                  else if (player.team === 'home') { actualTeam = game.home; teamLogo = game.homeLogo; }
                } else if (selectedMarket === 'totals') {
                  actualTeam = null;
                } else {
                  if (player.team === game.away) teamLogo = game.awayLogo;
                  else if (player.team === game.home) teamLogo = game.homeLogo;
                }
                const standaloneAllOdds = {};
                const standalonePattern = new RegExp(`^${selectedMarket}_(.+)${fieldSuffix}$`);
                Object.keys(player).forEach(k => {
                  const m = k.match(standalonePattern);
                  if (!m) return;
                  if (k.includes('_is') || k.includes('best') || k.includes('next')) return;
                  const raw = m[1];
                  const bk = BOOKMAKER_ALIASES[raw] || raw;
                  const v = player[k];
                  if (typeof v === 'number') {
                    standaloneAllOdds[bk] = v;
                    const avail = player[`${k}_isAvailable`];
                    if (avail !== undefined) standaloneAllOdds[`${bk}_isAvailable`] = avail;
                  }
                });

                bestBets.push({
                  player: displayName,
                  team: actualTeam,
                  teamLogo,
                  market: marketDisplayName,
                  marketKey: selectedMarket,
                  book: topSecondary.book,
                  odds: `${topSecondary.price > 0 ? '+' : ''}${topSecondary.price}`,
                  advantage: topEdge,
                  tier,
                  competingOdds: '',
                  game: `${game.away} @ ${game.home}`,
                  gameId: game.gameIDmlb,
                  playerId: player.playerId,
                  awayTeam: game.away,
                  homeTeam: game.home,
                  playerTeam: actualTeam,
                  gameTime: game.startTimeEST || game.gameTime,
                  gameStatus: game.status,
                  allOdds: standaloneAllOdds,
                  point: player.point,
                  starter: player.starter || false,
                  battingOrder: null,
                  awayPitcher: game.awayProbablePitcher?.fullName || 'TBD',
                  homePitcher: game.homeProbablePitcher?.fullName || 'TBD',
                  betType: player.betType || (player.point !== null && player.point !== undefined ? 'over' : null),
                  position: null,
                  homeConference: game.homeConferenceName,
                  awayConference: game.awayConferenceName,
                  homeRank: game.homeRank,
                  awayRank: game.awayRank,
                  // Additional Secondary books (beyond the one we surfaced as
                  // the row's `book`) still get the caret hint.
                  secondaryMatches: beats.filter(b => b.book !== topSecondary.book),
                  isSecondaryOnly: true,
                });
              }
            }
          }
        }
      }
    });
  });

  return bestBets.sort((a, b) => {
    // First sort by tier
    if (a.tier !== b.tier) {
      const tierOrder = { 'edge-one': 0, 'edge-two': 1, 'edge-three': 2, 'no-edge': 3 };
      return tierOrder[a.tier] - tierOrder[b.tier];
    }
    // Within the same tier, sort by advantage (edge percentage) descending
    return b.advantage - a.advantage;
  });
};